// @ts-nocheck — concept-preview page; original is docs/family-finance-app.jsx.
// This is a 1:1 copy for in-app preview at /family-finance-app and is not
// production code. Skipping TS strictness.
import { useState } from "react";
import {
  AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
  ScatterChart, Scatter, ComposedChart
} from "recharts";

const FONT = "https://fonts.googleapis.com/css2?family=Fraunces:wght@400;500;600;700&family=DM+Mono:wght@300;400;500&family=Outfit:wght@300;400;500;600;700&display=swap";

const C = {
  bg:"#0a0d12", surface:"#11161e", card:"#161d27", border:"#1e2937",
  gold:"#c9a84c", goldlt:"#e8c96a", teal:"#2dd4bf", lavender:"#a78bfa",
  rose:"#fb7185", amber:"#fbbf24", mint:"#34d399",
  red:"#f87171", green:"#4ade80",
  text:"#e5e9ee", sub:"#7d8898", muted:"#3d4a5c"
};

const fmt = (n) => new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",maximumFractionDigits:0}).format(n);
const fmtK = (n) => Math.abs(n) >= 1e6 ? `$${(n/1e6).toFixed(2)}M` : Math.abs(n) >= 1e3 ? `$${(n/1e3).toFixed(0)}K` : `$${n}`;
const pct = (n) => `${n > 0 ? "+" : ""}${n.toFixed(1)}%`;

// Layout primitives
const Card = ({ children, style, ...p }) => (
  <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:24, ...style }} {...p}>{children}</div>
);

const StatCard = ({ label, value, sub, color }) => (
  <Card style={{ flex:1, minWidth:160 }}>
    <div style={{ color:C.sub, fontSize:10, letterSpacing:2, textTransform:"uppercase", fontFamily:"Outfit", fontWeight:500, marginBottom:10 }}>{label}</div>
    <div style={{ color:color||C.text, fontSize:26, fontFamily:"Fraunces", fontWeight:600, lineHeight:1 }}>{value}</div>
    {sub && <div style={{ color:C.sub, fontSize:11, fontFamily:"DM Mono", marginTop:8 }}>{sub}</div>}
  </Card>
);

const SectionHeader = ({ title, sub, action }) => (
  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"end", marginBottom:20, gap:16 }}>
    <div>
      <div style={{ fontFamily:"Fraunces", fontSize:22, color:C.text, fontWeight:600, letterSpacing:-0.5 }}>{title}</div>
      {sub && <div style={{ fontFamily:"Outfit", fontSize:12, color:C.sub, marginTop:4 }}>{sub}</div>}
    </div>
    {action}
  </div>
);

const Pill = ({ children, color, bg }) => (
  <span style={{ background:bg, color, padding:"3px 9px", borderRadius:20, fontSize:10,
    fontFamily:"Outfit", fontWeight:500, letterSpacing:0.5, textTransform:"uppercase", whiteSpace:"nowrap" }}>{children}</span>
);

const ProgressBar = ({ pct, color, height=8 }) => (
  <div style={{ background:C.border, borderRadius:height/2, height, overflow:"hidden" }}>
    <div style={{ background:color, height, width:`${Math.min(pct,100)}%`, transition:"width .5s" }} />
  </div>
);

const btn = { background:C.gold, color:C.bg, border:"none", borderRadius:8, padding:"8px 16px", fontFamily:"Outfit", fontSize:12, fontWeight:600, cursor:"pointer" };
const btnGhost = { background:"transparent", color:C.sub, border:`1px solid ${C.border}`, borderRadius:8, padding:"8px 16px", fontFamily:"Outfit", fontSize:12, cursor:"pointer" };

const Tip = ({ active, payload, label }) => active && payload?.length ? (
  <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, padding:"8px 12px", fontFamily:"DM Mono", fontSize:12, color:C.text }}>
    {label && <div style={{ color:C.sub, fontSize:10, marginBottom:4 }}>{label}</div>}
    {payload.map((p,i) => <div key={i} style={{ color:p.color }}>{p.name}: {fmtK(p.value)}</div>)}
  </div>
) : null;

// ── DASHBOARD ──────────────────────────────────────────────────────────────
const NETWORTH_HIST = Array.from({length:24}, (_,i) => ({ m: new Date(2024,i,1).toLocaleDateString("en",{month:"short",year:"2-digit"}), nw: 720000 + i*15000 + Math.sin(i/3)*22000 }));

const ALERTS = [
  { sev:"critical", cat:"Tax", t:"Q2 estimated tax due Jun 15", body:"$8,400 estimated payment recommended", time:"2h" },
  { sev:"warning", cat:"Estate", t:"Beneficiary audit overdue", body:"Last verified Jan 2024 — 16 months ago", time:"1d" },
  { sev:"warning", cat:"Investment", t:"NVDA up 8.2% — concentration check", body:"Now 11.4% of taxable account, exceeds 10% threshold", time:"1d" },
  { sev:"info", cat:"Investment", t:"Tax-loss harvest: $8,600 available", body:"3 positions with unrealized losses", time:"2d" },
  { sev:"info", cat:"Tax", t:"NY 529 deduction: $3,500 remaining", body:"Contribute by Dec 31 to maximize", time:"3d" },
  { sev:"info", cat:"Bills", t:"Verizon Fios bill ready", body:"$132.45 — auto-pays May 12", time:"3d" },
  { sev:"info", cat:"Investment", t:"AAPL ex-dividend May 8", body:"Expecting $52 dividend payment", time:"4d" },
  { sev:"info", cat:"Documents", t:"Andre's passport expires Aug 2026", body:"Renewal recommended by Feb 2026", time:"1w" },
  { sev:"info", cat:"FIRE", t:"Net worth crossed $1M 🎉", body:"Coast FIRE at age 47 if you stop saving today", time:"2w" },
];

const MESSAGES = [
  { author:"Spouse", time:"2h", body:"I added the kitchen reno final receipts. Came in $4K under budget!", entity:"Kitchen Reno", read:false },
  { author:"Andre", time:"1d", body:"FYI Q2 estimated tax is due in 6 weeks — should we discuss the Roth conversion?", entity:"Tax 2026", read:true },
  { author:"Spouse", time:"3d", body:"Daughter's preschool deposit went through. $2,400 to budget.", entity:"Daughter", read:true },
];

const ACCOUNTS = [
  { id:1, name:"Merrill Edge Brokerage", inst:"Merrill", type:"Taxable", value:487420, sync:"plaid", color:C.gold },
  { id:2, name:"Merrill 401(k)", inst:"Merrill", type:"401(k)", value:612800, sync:"manual", color:C.teal },
  { id:3, name:"Roth IRA", inst:"Merrill", type:"Roth IRA", value:128500, sync:"plaid", color:C.lavender },
  { id:4, name:"HSA Investments", inst:"Fidelity", type:"HSA", value:42300, sync:"manual", color:C.amber },
  { id:5, name:"Daughter's 529", inst:"NY Direct", type:"529", value:41000, sync:"manual", color:C.mint },
  { id:6, name:"Daughter's UTMA", inst:"Merrill", type:"UTMA", value:8400, sync:"plaid", color:C.rose },
  { id:7, name:"Joint Checking", inst:"BoA", type:"Checking", value:42000, sync:"plaid", color:"#64748b" },
  { id:8, name:"High-Yield Savings", inst:"Marcus", type:"Savings", value:62500, sync:"plaid", color:"#94a3b8" },
  { id:9, name:"Coinbase Wallet", inst:"Coinbase", type:"Crypto", value:31200, sync:"snaptrade", color:"#f97316" },
  { id:10, name:"Co-op Mortgage", inst:"BoA", type:"Mortgage", value:-620000, sync:"plaid", color:C.red },
  { id:11, name:"HELOC", inst:"BoA", type:"HELOC", value:-45000, sync:"plaid", color:C.red },
  { id:12, name:"Credit Cards", inst:"Various", type:"Credit", value:-3400, sync:"plaid", color:C.red },
];

const ALLOC = [
  { name:"US Stocks", v:48, color:C.gold },
  { name:"Intl Stocks", v:18, color:C.teal },
  { name:"Bonds", v:14, color:C.lavender },
  { name:"Real Estate", v:11, color:C.amber },
  { name:"Crypto", v:3, color:C.rose },
  { name:"Cash", v:6, color:"#64748b" },
];

const Dashboard = ({ goTo }) => {
  const totalAssets = ACCOUNTS.filter(a => a.value>0).reduce((s,a)=>s+a.value,0);
  const totalLiab = ACCOUNTS.filter(a => a.value<0).reduce((s,a)=>s+Math.abs(a.value),0);
  const netWorth = totalAssets - totalLiab;
  const unread = MESSAGES.filter(m=>!m.read).length;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
      <SectionHeader title="Good evening, Andre" sub="Saturday, May 2, 2026 · Markets closed" />

      <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
        <StatCard label="Net Worth" value={fmtK(netWorth)} sub="+$67K YTD · +7.4%" color={C.gold} />
        <StatCard label="Savings Rate" value="42%" sub="On track for FI by 52" color={C.green} />
        <StatCard label="FIRE Progress" value="38%" sub={`${fmtK(1530000)} to Regular FIRE`} color={C.teal} />
        <StatCard label="Liquid" value={fmtK(104500)} sub="Emergency fund: 8.4 months" color={C.amber} />
      </div>

      <Card>
        <SectionHeader title="Net Worth" sub="24-month trajectory · Crossed $1M milestone Apr 2026" />
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={NETWORTH_HIST}>
            <defs>
              <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.gold} stopOpacity={0.35}/>
                <stop offset="100%" stopColor={C.gold} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="m" tick={{fill:C.sub,fontSize:10,fontFamily:"Outfit"}} axisLine={false} tickLine={false} interval={2}/>
            <YAxis tickFormatter={fmtK} tick={{fill:C.sub,fontSize:10,fontFamily:"DM Mono"}} axisLine={false} tickLine={false} width={56}/>
            <Tooltip content={<Tip />} />
            <ReferenceLine y={1000000} stroke={C.green} strokeDasharray="3 3" label={{ value:"$1M", fill:C.green, fontSize:10, fontFamily:"DM Mono" }} />
            <Area type="monotone" dataKey="nw" name="Net Worth" stroke={C.gold} strokeWidth={2.5} fill="url(#g1)" />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
        <Card style={{ flex:1, minWidth:280, cursor:"pointer" }} onClick={() => goTo("alerts")}>
          <SectionHeader title="Alerts" sub={`${ALERTS.length} active · 1 critical · 2 warnings`} />
          {ALERTS.slice(0,4).map((a,i) => (
            <div key={i} style={{ display:"flex", gap:10, padding:"10px 0", borderBottom: i<3?`1px solid ${C.border}`:"none" }}>
              <div style={{ color: a.sev==="critical"?C.red:a.sev==="warning"?C.amber:C.teal, fontSize:14, marginTop:1 }}>
                {a.sev==="critical"?"⚠":a.sev==="warning"?"◆":"●"}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:"Outfit", fontSize:13, color:C.text, fontWeight:500 }}>{a.t}</div>
                <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginTop:2 }}>{a.body}</div>
              </div>
              <div style={{ fontFamily:"Outfit", fontSize:10, color:C.muted }}>{a.time}</div>
            </div>
          ))}
        </Card>

        <Card style={{ flex:1, minWidth:280, cursor:"pointer" }} onClick={() => goTo("messages")}>
          <SectionHeader title="Messages" sub={unread>0 ? `${unread} unread from spouse` : "Up to date"} />
          {MESSAGES.slice(0,3).map((m,i) => (
            <div key={i} style={{ padding:"10px 0", borderBottom: i<2?`1px solid ${C.border}`:"none" }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                <span style={{ fontFamily:"Outfit", fontSize:12, color: m.read?C.sub:C.gold, fontWeight:500 }}>{m.author}</span>
                <span style={{ fontFamily:"Outfit", fontSize:10, color:C.muted }}>{m.time}</span>
              </div>
              <div style={{ fontFamily:"Outfit", fontSize:12, color:C.text, lineHeight:1.4 }}>{m.body}</div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
};

// ── FIRE PAGE (NEW) ─────────────────────────────────────────────────────────
const SAVINGS_RATE_TABLE = [
  { rate:5, years:66 }, { rate:10, years:51 }, { rate:15, years:43 },
  { rate:20, years:37 }, { rate:25, years:32 }, { rate:30, years:28 },
  { rate:35, years:25 }, { rate:42, years:21, current:true },
  { rate:50, years:17 }, { rate:60, years:12.5 }, { rate:70, years:8.5 }, { rate:80, years:5.5 },
];

const FIRE_NUMBERS = [
  { type:"Lean FIRE",    target:1100000, current:1003020, color:C.green,  desc:"$45K/yr lifestyle · 25× lean spending" },
  { type:"Coast FIRE",   target:680000,  current:1003020, color:C.teal,   desc:"Stop saving today, coast to FIRE at 65", achieved:true },
  { type:"Barista FIRE", target:1800000, current:1003020, color:C.amber,  desc:"Part-time job covers half of expenses" },
  { type:"Regular FIRE", target:2533020, current:1003020, color:C.gold,   desc:"$101K/yr current spending · 25× current" },
  { type:"Fat FIRE",     target:4500000, current:1003020, color:C.lavender,desc:"$180K/yr lifestyle target" },
];

const FIRE_PROJ = Array.from({length:35}, (_,i) => ({
  age: 38+i,
  conservative: Math.round(1003020 * Math.pow(1.05, i) + 36000 * ((Math.pow(1.05,i)-1)/0.05)),
  expected:     Math.round(1003020 * Math.pow(1.07, i) + 36000 * ((Math.pow(1.07,i)-1)/0.07)),
  optimistic:   Math.round(1003020 * Math.pow(1.09, i) + 36000 * ((Math.pow(1.09,i)-1)/0.09)),
}));

const TRINITY_RESULTS = [
  { period:"1929-1958", success:false, terminal:0.1 },
  { period:"1937-1966", success:false, terminal:0.6 },
  { period:"1966-1995", success:false, terminal:0.3 },
  { period:"1972-2001", success:true, terminal:1.4 },
  { period:"1982-2011", success:true, terminal:8.7 },
  { period:"1990-2019", success:true, terminal:5.2 },
  { period:"1995-2024", success:true, terminal:4.8 },
];

const FIRE = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Financial Independence" sub="The path to optionality" />

    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="Savings Rate" value="42%" sub="Annual: $108K saved on $258K take-home" color={C.green} />
      <StatCard label="Years to FI" value="21" sub="At current savings rate" color={C.gold} />
      <StatCard label="FI Date" value="Mar 2047" sub="Age 52" color={C.teal} />
      <StatCard label="Annual Spending" value={fmtK(101321)} sub="Current burn rate" />
    </div>

    <Card>
      <SectionHeader title="The FIRE Numbers" sub="Multiple targets · You've already hit Coast FIRE" />
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        {FIRE_NUMBERS.map((f,i) => {
          const p = Math.min((f.current/f.target)*100, 100);
          return (
            <div key={i}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:6 }}>
                <div style={{ display:"flex", gap:10, alignItems:"baseline" }}>
                  <span style={{ fontFamily:"Fraunces", fontSize:16, color:C.text, fontWeight:600 }}>{f.type}</span>
                  {f.achieved && <Pill color={C.green} bg={`${C.green}22`}>✓ Achieved</Pill>}
                  <span style={{ fontFamily:"Outfit", fontSize:11, color:C.sub }}>{f.desc}</span>
                </div>
                <div style={{ fontFamily:"DM Mono", fontSize:13, color:f.color }}>
                  {fmtK(f.current)} / {fmtK(f.target)} · {p.toFixed(0)}%
                </div>
              </div>
              <ProgressBar pct={p} color={f.color} />
            </div>
          );
        })}
      </div>
    </Card>

    <Card>
      <SectionHeader title="The Shockingly Simple Math" sub={`Mr. Money Mustache's table — your savings rate is what matters`} />
      <div style={{ background:C.surface, padding:14, borderRadius:8, marginBottom:16, fontSize:12, fontFamily:"Outfit", color:C.sub, lineHeight:1.5 }}>
        Years to financial independence depends almost entirely on your savings rate, not income. Your current 42% rate puts you at 21 years. Each extra percentage point shaves ~6 months.
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={SAVINGS_RATE_TABLE}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
          <XAxis dataKey="rate" tickFormatter={(v) => `${v}%`} tick={{fill:C.sub,fontSize:10,fontFamily:"DM Mono"}} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={(v) => `${v}y`} tick={{fill:C.sub,fontSize:10,fontFamily:"DM Mono"}} axisLine={false} tickLine={false} width={40} />
          <Tooltip content={<Tip />} />
          <Bar dataKey="years" name="Years to FI" radius={[4,4,0,0]}>
            {SAVINGS_RATE_TABLE.map((d,i) => <Cell key={i} fill={d.current ? C.gold : C.muted} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>

    <Card>
      <SectionHeader title="Portfolio Projection" sub="Conservative (5%) · Expected (7%) · Optimistic (9%) real returns" />
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={FIRE_PROJ}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
          <XAxis dataKey="age" tick={{fill:C.sub,fontSize:10,fontFamily:"Outfit"}} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={fmtK} tick={{fill:C.sub,fontSize:10,fontFamily:"DM Mono"}} axisLine={false} tickLine={false} width={62} />
          <Tooltip content={<Tip />} />
          <Area type="monotone" dataKey="optimistic" name="Optimistic" stroke={C.green} fill={C.green} fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="3 3" />
          <Area type="monotone" dataKey="expected" name="Expected" stroke={C.gold} fill={C.gold} fillOpacity={0.2} strokeWidth={2.5} />
          <Area type="monotone" dataKey="conservative" name="Conservative" stroke={C.amber} fill={C.amber} fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="3 3" />
          <ReferenceLine y={2533020} stroke={C.gold} strokeDasharray="2 2" label={{ value:"FIRE", fill:C.gold, fontSize:10 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </Card>

    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <Card style={{ flex:1, minWidth:300 }}>
        <SectionHeader title="Withdrawal Strategy" sub="Currently modeling: Guyton-Klinger guardrails" />
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {[
            { n:"4% Rule (Bengen)",       w:"$101K/yr", c:false, d:"Static, fully inflation-adjusted" },
            { n:"Guyton-Klinger Guardrails", w:"$95-118K", c:true,  d:"±10% adjust at ±20% bands" },
            { n:"VPW (Bogleheads)",       w:"$104K Y1",   c:false, d:"% rises with age, never depletes" },
            { n:"CAPE-based",             w:"$92K (CAPE 32)", c:false, d:"Adjusts to market valuation" },
          ].map((s,i) => (
            <div key={i} style={{ padding:12, background:s.c?`${C.gold}11`:C.surface, border:s.c?`1px solid ${C.gold}66`:`1px solid ${C.border}`, borderRadius:8 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text, fontWeight:500 }}>{s.n}</span>
                <span style={{ fontFamily:"DM Mono", fontSize:12, color:s.c?C.gold:C.sub }}>{s.w}</span>
              </div>
              <span style={{ fontFamily:"Outfit", fontSize:11, color:C.sub }}>{s.d}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card style={{ flex:1, minWidth:300 }}>
        <SectionHeader title="Trinity Study Back-Test" sub="Your portfolio replayed through history · 71% success rate" />
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {TRINITY_RESULTS.map((t,i) => (
            <div key={i} style={{ display:"grid", gridTemplateColumns:"100px 100px 1fr 60px", padding:"8px 0", borderBottom: i<6?`1px solid ${C.border}`:"none", alignItems:"center" }}>
              <span style={{ fontFamily:"DM Mono", fontSize:11, color:C.sub }}>{t.period}</span>
              <Pill color={t.success?C.green:C.red} bg={`${t.success?C.green:C.red}22`}>
                {t.success ? "Survived" : "Depleted"}
              </Pill>
              <span style={{ fontFamily:"Outfit", fontSize:11, color:C.sub }}>Terminal multiplier</span>
              <span style={{ fontFamily:"DM Mono", fontSize:12, color:t.success?C.green:C.red, textAlign:"right" }}>
                {t.terminal.toFixed(1)}×
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>

    <Card>
      <SectionHeader title="Geographic Arbitrage" sub="Cost-of-living comparisons for early retirement" />
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:14 }}>
        {[
          { city:"Manhattan, NY", cur:true, idx:187, save:0 },
          { city:"Austin, TX", idx:103, save:31000 },
          { city:"Asheville, NC", idx:88, save:42000 },
          { city:"Lisbon, Portugal", idx:65, save:62000 },
          { city:"Mexico City", idx:48, save:74000 },
        ].map((c,i) => (
          <div key={i} style={{ padding:14, background:c.cur?`${C.gold}11`:C.surface, border:`1px solid ${c.cur?C.gold:C.border}`, borderRadius:8 }}>
            <div style={{ fontFamily:"Outfit", fontSize:13, color:C.text, fontWeight:500 }}>{c.city}</div>
            {c.cur && <Pill color={C.gold} bg={`${C.gold}22`}>Current</Pill>}
            <div style={{ fontFamily:"DM Mono", fontSize:18, color:c.cur?C.gold:C.green, marginTop:8 }}>{c.idx}</div>
            <div style={{ fontFamily:"Outfit", fontSize:10, color:C.sub, marginTop:2 }}>COL Index</div>
            {!c.cur && <div style={{ fontFamily:"DM Mono", fontSize:11, color:C.green, marginTop:6 }}>−{fmt(c.save)}/yr</div>}
          </div>
        ))}
      </div>
    </Card>
  </div>
);

// ── ALERTS ──────────────────────────────────────────────────────────────────
const Alerts = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Alerts" sub={`${ALERTS.length} active · 1 critical · 2 warnings`}
      action={<button style={btnGhost}>Configure</button>} />
    <Card style={{ padding:0 }}>
      {ALERTS.map((a,i) => (
        <div key={i} style={{ display:"flex", gap:14, padding:"18px 24px", borderBottom: i<ALERTS.length-1?`1px solid ${C.border}`:"none", alignItems:"start" }}>
          <div style={{ color: a.sev==="critical"?C.red:a.sev==="warning"?C.amber:C.teal, fontSize:18 }}>
            {a.sev==="critical"?"⚠":a.sev==="warning"?"◆":"●"}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:4 }}>
              <span style={{ fontFamily:"Outfit", fontSize:14, color:C.text, fontWeight:500 }}>{a.t}</span>
              <Pill color={C.sub} bg={C.surface}>{a.cat}</Pill>
            </div>
            <div style={{ fontFamily:"Outfit", fontSize:12, color:C.sub }}>{a.body}</div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"end", gap:6 }}>
            <span style={{ fontFamily:"Outfit", fontSize:10, color:C.muted }}>{a.time}</span>
            <button style={{ ...btnGhost, padding:"4px 10px", fontSize:10 }}>Dismiss</button>
          </div>
        </div>
      ))}
    </Card>
  </div>
);

// ── MESSAGES ────────────────────────────────────────────────────────────────
const DECISIONS = [
  { title:"Delay second car to 2027", date:"Apr 2026", rev:"Jan 2027", body:"Daughter starting pre-K, prefer to bank the $45K toward college fund instead." },
  { title:"Max 401k + Mega Backdoor", date:"Jan 2026", rev:"Jan 2027", body:"Confirmed plan allows after-tax contributions and in-plan Roth conversion." },
  { title:"NY 529 over Vanguard 529", date:"Jun 2024", rev:"-", body:"NY state deduction worth ~$660/yr beats slightly higher Vanguard performance." },
];
const Messages = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Notes & Messages" sub="Private to household members" action={<button style={btn}>+ New</button>} />
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <Card style={{ flex:2, minWidth:340 }}>
        <div style={{ fontFamily:"Fraunces", fontSize:16, color:C.text, fontWeight:600, marginBottom:16 }}>Conversation</div>
        {MESSAGES.map((m,i) => (
          <div key={i} style={{ display:"flex", flexDirection:"column", alignItems: m.author==="Andre"?"flex-end":"flex-start", marginBottom:14 }}>
            <div style={{ background: m.author==="Andre"?C.surface:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px", maxWidth:"80%" }}>
              <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:6 }}>
                <span style={{ fontFamily:"Outfit", fontSize:11, color:m.author==="Andre"?C.gold:C.teal, fontWeight:500 }}>{m.author}</span>
                <span style={{ fontFamily:"Outfit", fontSize:10, color:C.muted }}>{m.time}</span>
              </div>
              <div style={{ fontFamily:"Outfit", fontSize:13, color:C.text, lineHeight:1.5 }}>{m.body}</div>
              {m.entity && <div style={{ marginTop:8 }}><Pill color={C.sub} bg={C.bg}>📎 {m.entity}</Pill></div>}
            </div>
          </div>
        ))}
        <div style={{ marginTop:20, padding:14, background:C.surface, borderRadius:10, border:`1px solid ${C.border}` }}>
          <input placeholder="Reply..." style={{ width:"100%", background:"transparent", border:"none", color:C.text, fontFamily:"Outfit", fontSize:13, outline:"none" }} />
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:10 }}>
            <button style={{ ...btnGhost, padding:"4px 10px", fontSize:10 }}>📎 Attach record</button>
            <button style={{ ...btn, padding:"4px 14px", fontSize:11 }}>Send</button>
          </div>
        </div>
      </Card>
      <Card style={{ flex:1, minWidth:280 }}>
        <SectionHeader title="Decision Log" sub="What we agreed and why" />
        {DECISIONS.map((d,i) => (
          <div key={i} style={{ padding:"12px 0", borderBottom: i<DECISIONS.length-1?`1px solid ${C.border}`:"none" }}>
            <div style={{ fontFamily:"Outfit", fontSize:13, color:C.text, fontWeight:500 }}>{d.title}</div>
            <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginTop:4, lineHeight:1.4 }}>{d.body}</div>
            <div style={{ display:"flex", gap:8, marginTop:8 }}>
              <Pill color={C.gold} bg={`${C.gold}22`}>{d.date}</Pill>
              {d.rev !== "-" && <Pill color={C.sub} bg={C.surface}>Revisit {d.rev}</Pill>}
            </div>
          </div>
        ))}
      </Card>
    </div>
  </div>
);

// ── NET WORTH (full page) ───────────────────────────────────────────────────
const NetWorth = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Net Worth" sub="Complete picture · Updated daily" />
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="Net Worth" value={fmtK(1003020)} sub="+$67K YTD · +7.4%" color={C.gold} />
      <StatCard label="Total Assets" value={fmtK(1671420)} color={C.green} />
      <StatCard label="Liabilities" value={fmtK(668400)} color={C.red} />
      <StatCard label="Debt-to-Asset" value="40%" sub="Healthy: under 50%" color={C.teal} />
    </div>
    <Card>
      <SectionHeader title="24-Month Trajectory" sub="With key milestones" />
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={NETWORTH_HIST}>
          <defs><linearGradient id="nw" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.gold} stopOpacity={0.4}/><stop offset="100%" stopColor={C.gold} stopOpacity={0}/></linearGradient></defs>
          <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
          <XAxis dataKey="m" tick={{fill:C.sub,fontSize:10,fontFamily:"Outfit"}} axisLine={false} tickLine={false} interval={2}/>
          <YAxis tickFormatter={fmtK} tick={{fill:C.sub,fontSize:10,fontFamily:"DM Mono"}} axisLine={false} tickLine={false} width={56}/>
          <Tooltip content={<Tip />} />
          <ReferenceLine y={1000000} stroke={C.green} strokeDasharray="3 3" label={{ value:"$1M ✓", fill:C.green, fontSize:10 }} />
          <ReferenceLine y={750000} stroke={C.muted} strokeDasharray="3 3" />
          <Area type="monotone" dataKey="nw" name="Net Worth" stroke={C.gold} strokeWidth={2.5} fill="url(#nw)" />
        </AreaChart>
      </ResponsiveContainer>
    </Card>
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <Card style={{ flex:1, minWidth:280 }}>
        <SectionHeader title="Assets" sub={`${fmtK(1671420)} total`} />
        {[
          { l:"Investment Accounts", v:1352120, c:C.gold },
          { l:"Co-op (Equity)", v:830000, c:C.amber },
          { l:"Cash & Savings", v:104500, c:C.teal },
          { l:"Crypto", v:31200, c:C.rose },
        ].map((a,i) => (
          <div key={i} style={{ marginBottom:14 }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
              <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text }}>{a.l}</span>
              <span style={{ fontFamily:"DM Mono", fontSize:13, color:a.c }}>{fmt(a.v)}</span>
            </div>
            <ProgressBar pct={(a.v/1671420)*100} color={a.c} />
          </div>
        ))}
      </Card>
      <Card style={{ flex:1, minWidth:280 }}>
        <SectionHeader title="Liabilities" sub={`${fmtK(668400)} total`} />
        {[
          { l:"Co-op Mortgage", v:620000, c:C.red },
          { l:"HELOC", v:45000, c:C.red },
          { l:"Credit Cards", v:3400, c:C.amber },
          { l:"Student Loans", v:0, c:C.muted },
        ].map((a,i) => (
          <div key={i} style={{ marginBottom:14 }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
              <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text }}>{a.l}</span>
              <span style={{ fontFamily:"DM Mono", fontSize:13, color:a.c }}>{fmt(a.v)}</span>
            </div>
            <ProgressBar pct={a.v ? (a.v/668400)*100 : 1} color={a.c} />
          </div>
        ))}
      </Card>
    </div>
  </div>
);

// ── GOALS ────────────────────────────────────────────────────────────────────
const GOALS = [
  { name:"Emergency Fund", t:60000, c:48500, due:"Dec '26", color:C.teal, monthly:1000 },
  { name:"Next Car", t:45000, c:18000, due:"Aug '27", color:C.amber, monthly:1500 },
  { name:"Kitchen Reno", t:80000, c:32000, due:"Jun '27", color:C.gold, monthly:3500 },
  { name:"Daughter College", t:300000, c:41000, due:"Aug '41", color:C.mint, monthly:420 },
  { name:"Sabbatical Fund", t:120000, c:14000, due:"Jan '30", color:C.lavender, monthly:2000 },
];
const Goals = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Savings Goals" sub="5 active · $8,420/mo combined" action={<button style={btn}>+ New Goal</button>} />
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:14 }}>
      {GOALS.map((g,i) => {
        const p = (g.c/g.t)*100;
        return (
          <Card key={i} style={{ borderTop:`3px solid ${g.color}` }}>
            <div style={{ fontFamily:"Fraunces", fontSize:18, color:C.text, fontWeight:600 }}>{g.name}</div>
            <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginTop:4 }}>Due {g.due} · {fmt(g.monthly)}/mo</div>
            <div style={{ display:"flex", justifyContent:"space-between", marginTop:18, marginBottom:8 }}>
              <span style={{ fontFamily:"DM Mono", fontSize:13, color:g.color }}>{fmt(g.c)}</span>
              <span style={{ fontFamily:"DM Mono", fontSize:13, color:C.sub }}>{fmt(g.t)}</span>
            </div>
            <ProgressBar pct={p} color={g.color} />
            <div style={{ display:"flex", justifyContent:"space-between", marginTop:10, fontFamily:"Outfit", fontSize:11, color:C.sub }}>
              <span>{p.toFixed(0)}% funded</span>
              <span>{fmt(g.t-g.c)} to go</span>
            </div>
          </Card>
        );
      })}
    </div>
  </div>
);

// ── RETIREMENT ─────────────────────────────────────────────────────────────
const Retirement = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Retirement Planning" sub="Multi-scenario · Monte Carlo · 1,000 simulations" />
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="Confidence" value="87%" sub="Monte Carlo success rate" color={C.green} />
      <StatCard label="Projected at 65" value={fmtK(7100000)} color={C.gold} />
      <StatCard label="Annual Income" value={fmtK(284000)} sub="4% withdrawal" />
      <StatCard label="Bridge to SS" value="3 yrs" sub="Age 65→68" color={C.teal} />
    </div>

    <Card>
      <SectionHeader title="Compound Projection" sub="Base case scenario" />
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={FIRE_PROJ}>
          <defs><linearGradient id="ret" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.green} stopOpacity={0.35}/><stop offset="100%" stopColor={C.green} stopOpacity={0}/></linearGradient></defs>
          <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
          <XAxis dataKey="age" tick={{fill:C.sub,fontSize:10}} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={fmtK} tick={{fill:C.sub,fontSize:10,fontFamily:"DM Mono"}} axisLine={false} tickLine={false} width={62} />
          <Tooltip content={<Tip />} />
          <Area type="monotone" dataKey="expected" name="Balance" stroke={C.green} strokeWidth={2.5} fill="url(#ret)" />
        </AreaChart>
      </ResponsiveContainer>
    </Card>

    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <Card style={{ flex:1, minWidth:260 }}>
        <SectionHeader title="Saved Scenarios" />
        {[
          {n:"Base Case", v:7100000, a:true, c:C.gold},
          {n:"Aggressive (10% return)", v:9800000, c:C.green},
          {n:"Sabbatical 2030", v:6200000, c:C.amber},
          {n:"Second Child", v:5800000, c:C.lavender},
          {n:"FIRE at 52", v:2533000, c:C.teal},
        ].map((s,i) => (
          <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:i<4?`1px solid ${C.border}`:"none" }}>
            <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text }}>{s.n} {s.a && <span style={{color:C.gold}}>●</span>}</span>
            <span style={{ fontFamily:"DM Mono", fontSize:12, color:s.c }}>{fmtK(s.v)}</span>
          </div>
        ))}
      </Card>
      <Card style={{ flex:1, minWidth:260 }}>
        <SectionHeader title="Income Sources at 65" />
        {[
          {l:"Portfolio (4%)", v:284000, c:C.gold},
          {l:"Social Security (Andre)", v:42000, c:C.teal},
          {l:"Social Security (Spouse)", v:34000, c:C.lavender},
          {l:"Pension", v:0, c:C.muted},
        ].map((s,i) => (
          <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 0", borderBottom:i<3?`1px solid ${C.border}`:"none" }}>
            <div style={{ width:6, height:6, borderRadius:"50%", background:s.c }} />
            <span style={{ flex:1, fontFamily:"Outfit", fontSize:13, color:C.text }}>{s.l}</span>
            <span style={{ fontFamily:"DM Mono", fontSize:12, color:C.text }}>{fmt(s.v)}/yr</span>
          </div>
        ))}
      </Card>
      <Card style={{ flex:1, minWidth:260 }}>
        <SectionHeader title="Spending Smile" sub="Real retiree spending isn't flat" />
        <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginBottom:14, lineHeight:1.5 }}>
          Blanchett research: Go-go years (65-75), Slow-go (75-85), No-go (85+). Default flat-spend models often over-save.
        </div>
        <ResponsiveContainer width="100%" height={140}>
          <LineChart data={[
            {a:65,s:120}, {a:70,s:110}, {a:75,s:95}, {a:80,s:88}, {a:85,s:92}, {a:90,s:105}, {a:95,s:110}
          ]}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="a" tick={{fill:C.sub,fontSize:9}} axisLine={false} tickLine={false} />
            <YAxis hide />
            <Line type="monotone" dataKey="s" stroke={C.gold} strokeWidth={2.5} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Card>
    </div>
  </div>
);

// ── TAX ─────────────────────────────────────────────────────────────────────
const Tax = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Tax Planning" sub="2026 projection · Federal + NY + NYC"
      action={<button style={btn}>Generate CPA Package</button>} />
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="Projected Liability" value={fmt(148800)} sub="Fed + NY + NYC" color={C.gold} />
      <StatCard label="Withheld YTD" value={fmt(124000)} color={C.teal} />
      <StatCard label="Projected Owed" value={fmt(24800)} color={C.amber} sub="⚠ Below safe harbor" />
      <StatCard label="Effective Rate" value="30.7%" sub="Marginal: 37%" />
    </div>

    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <Card style={{ flex:1, minWidth:280 }}>
        <SectionHeader title="Tax-Advantaged Contributions" />
        {[
          { l:"401(k) Traditional", c:18500, m:23500, color:C.teal },
          { l:"Mega Backdoor Roth", c:0, m:46500, color:C.lavender, badge:"OPPORTUNITY" },
          { l:"HSA", c:4150, m:8550, color:C.amber },
          { l:"NY 529 (deductible)", c:6500, m:10000, color:C.mint },
          { l:"Backdoor Roth IRA", c:7000, m:7000, color:C.gold },
        ].map((x,i) => (
          <div key={i} style={{ marginBottom:14 }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text }}>{x.l}</span>
                {x.badge && <Pill color={C.lavender} bg={`${C.lavender}22`}>{x.badge}</Pill>}
              </div>
              <span style={{ fontFamily:"DM Mono", fontSize:12, color:x.color }}>{fmt(x.c)} / {fmt(x.m)}</span>
            </div>
            <ProgressBar pct={(x.c/x.m)*100} color={x.color} />
          </div>
        ))}
      </Card>

      <Card style={{ flex:1, minWidth:280 }}>
        <SectionHeader title="Capital Gains" />
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
          {[
            {l:"Realized YTD",v:4200,c:C.text},
            {l:"Harvestable Loss",v:-8600,c:C.green},
            {l:"Unrealized Short",v:18400,c:C.sub},
            {l:"Unrealized Long",v:312000,c:C.gold},
          ].map((x,i) => (
            <div key={i}>
              <div style={{ fontFamily:"Outfit", fontSize:9, color:C.sub, letterSpacing:1.5, textTransform:"uppercase" }}>{x.l}</div>
              <div style={{ fontFamily:"DM Mono", fontSize:18, color:x.c, marginTop:4 }}>{fmt(x.v)}</div>
            </div>
          ))}
        </div>
        <div style={{ padding:14, background:`${C.amber}11`, borderRadius:8, border:`1px solid ${C.amber}44` }}>
          <div style={{ fontFamily:"Outfit", fontSize:12, color:C.amber, fontWeight:500 }}>◆ Harvest opportunity</div>
          <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginTop:4, lineHeight:1.5 }}>3 positions = $8,600 unrealized loss. Selling could offset realized gains. ⚠ Watch wash-sale window.</div>
        </div>
      </Card>
    </div>

    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <Card style={{ flex:1, minWidth:300 }}>
        <SectionHeader title="Roth Conversion Ladder" sub="For early-retirement access" />
        <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginBottom:14, lineHeight:1.5 }}>
          Convert traditional → Roth in low-income years; access converted funds 5 years later penalty-free. Plan multi-year.
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={[
            {y:"2026",c:0,b:18500},{y:"2027",c:0,b:18500},
            {y:"FIRE+1",c:50000,b:0},{y:"FIRE+2",c:50000,b:0},
            {y:"FIRE+3",c:50000,b:0},{y:"FIRE+4",c:50000,b:0},
          ]}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="y" tick={{fill:C.sub,fontSize:9}} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={fmtK} tick={{fill:C.sub,fontSize:9,fontFamily:"DM Mono"}} axisLine={false} tickLine={false} width={42} />
            <Bar dataKey="b" stackId="a" fill={C.teal} name="Build" />
            <Bar dataKey="c" stackId="a" fill={C.lavender} name="Convert" />
          </BarChart>
        </ResponsiveContainer>
      </Card>
      <Card style={{ flex:1, minWidth:300 }}>
        <SectionHeader title="ACA Subsidy Planner" sub="Critical for early retirement (pre-65)" />
        <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginBottom:14, lineHeight:1.5 }}>
          Stay under 400% FPL ($120K MAGI for family of 3) for ACA subsidies. Roth conversions count as MAGI.
        </div>
        <div style={{ background:C.surface, padding:14, borderRadius:8 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:10 }}>
            <span style={{ fontFamily:"Outfit", fontSize:11, color:C.sub }}>Projected MAGI in early retirement</span>
            <span style={{ fontFamily:"DM Mono", fontSize:13, color:C.green }}>$58,000</span>
          </div>
          <ProgressBar pct={48} color={C.green} />
          <div style={{ fontFamily:"Outfit", fontSize:10, color:C.muted, marginTop:8 }}>48% of 400% FPL · Eligible for ~$8K/yr subsidy</div>
        </div>
      </Card>
    </div>

    <Card>
      <SectionHeader title="Tax Document Checklist" sub="2025 filing · 7 of 12 received" />
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:10 }}>
        {[
          {n:"W-2 (Andre)", r:true},
          {n:"W-2 (Spouse)", r:true},
          {n:"1099-DIV (Merrill)", r:true},
          {n:"1099-B (Merrill)", r:true},
          {n:"1099-INT (BoA)", r:true},
          {n:"1098 Mortgage", r:true},
          {n:"1098-T", r:false},
          {n:"5498 (Roth IRA)", r:false},
          {n:"5498 (HSA)", r:false},
          {n:"K-1 (Private Inv)", r:false},
          {n:"1099-NEC (Side)", r:true},
          {n:"NY IT-201 estimate", r:false},
        ].map((d,i) => (
          <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 0" }}>
            <div style={{ width:14, height:14, borderRadius:3, background:d.r?C.green:"transparent", border:`1px solid ${d.r?C.green:C.border}`, display:"flex", alignItems:"center", justifyContent:"center", color:C.bg, fontSize:10 }}>{d.r?"✓":""}</div>
            <span style={{ fontFamily:"Outfit", fontSize:12, color:d.r?C.text:C.sub }}>{d.n}</span>
          </div>
        ))}
      </div>
    </Card>
  </div>
);

// ── SCENARIOS ───────────────────────────────────────────────────────────────
const Scenarios = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="What-If Scenarios" sub="Stress-test major life decisions" action={<button style={btn}>+ New Scenario</button>} />
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:14 }}>
      {[
        {n:"Both Lose Jobs (6 mo)", impact:-180000, status:"Survivable", c:C.green, runway:"14 months emergency cash"},
        {n:"Sabbatical Year 2030", impact:-260000, status:"Achievable", c:C.amber, runway:"Saving $2K/mo earmark"},
        {n:"Second Child", impact:-520000, status:"Modeling", c:C.lavender, runway:"FIRE delays 3 years"},
        {n:"Move to Suburbs", impact:+340000, status:"Net positive", c:C.green, runway:"Lower COL offsets value lost"},
        {n:"$50K Emergency", impact:-50000, status:"Easy", c:C.green, runway:"Pull from HYSA + brokerage"},
        {n:"Rates Drop 1%", impact:+85000, status:"Refi triggered", c:C.teal, runway:"Saves $310/mo"},
      ].map((s,i) => (
        <Card key={i}>
          <div style={{ fontFamily:"Fraunces", fontSize:16, color:C.text, fontWeight:600 }}>{s.n}</div>
          <div style={{ display:"flex", gap:8, marginTop:8 }}>
            <Pill color={s.c} bg={`${s.c}22`}>{s.status}</Pill>
          </div>
          <div style={{ fontFamily:"DM Mono", fontSize:18, color:s.impact>0?C.green:C.red, marginTop:14 }}>
            {s.impact>0?"+":""}{fmt(s.impact)}
          </div>
          <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginTop:6, lineHeight:1.5 }}>
            {s.runway}
          </div>
        </Card>
      ))}
    </div>
  </div>
);

// ── CASH FLOW ──────────────────────────────────────────────────────────────
const CashFlow = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Cash Flow" sub="12-month forecast · Spending vs. saving" />
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="Monthly Inflow" value={fmt(28400)} sub="Take-home" color={C.green}/>
      <StatCard label="Monthly Outflow" value={fmt(19800)} sub="All spending"/>
      <StatCard label="Net Saved" value={fmt(8600)} color={C.gold} sub="42% rate"/>
      <StatCard label="Lifestyle Inflation" value="+4.2%" sub="vs. last year" color={C.amber}/>
    </div>
    <Card>
      <SectionHeader title="12-Month Projection" sub="Forecasted cash flow" />
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={Array.from({length:12},(_,i) => ({m:["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i], in:28400, out:19800+(i===6?5000:0)+(i===11?8000:0), net:8600-(i===6?5000:0)-(i===11?8000:0)}))}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
          <XAxis dataKey="m" tick={{fill:C.sub,fontSize:10}} axisLine={false} tickLine={false}/>
          <YAxis tickFormatter={fmtK} tick={{fill:C.sub,fontSize:10,fontFamily:"DM Mono"}} axisLine={false} tickLine={false} width={56}/>
          <Tooltip content={<Tip/>}/>
          <Bar dataKey="in" name="Income" fill={C.green} />
          <Bar dataKey="out" name="Expenses" fill={C.red} />
          <Line type="monotone" dataKey="net" name="Net Saved" stroke={C.gold} strokeWidth={3} dot={{ fill:C.gold, r:4 }}/>
        </ComposedChart>
      </ResponsiveContainer>
    </Card>
    <Card>
      <SectionHeader title="Spending by Category" sub="Last 30 days · vs. budget" />
      {[
        {c:"Housing (Maintenance + HELOC)", a:3340, b:3500, color:C.gold},
        {c:"Groceries", a:1240, b:1200, color:C.teal},
        {c:"Daughter (preschool, activities)", a:2400, b:2400, color:C.mint},
        {c:"Dining Out", a:680, b:500, color:C.amber, over:true},
        {c:"Transportation", a:340, b:400, color:C.lavender},
        {c:"Utilities", a:425, b:450, color:"#94a3b8"},
        {c:"Entertainment & Subs", a:280, b:300, color:C.rose},
        {c:"Personal & Health", a:520, b:600, color:"#64748b"},
      ].map((c,i) => (
        <div key={i} style={{ marginBottom:14 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
            <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text }}>
              {c.c} {c.over && <span style={{ color:C.amber, fontSize:11 }}>⚠ over</span>}
            </span>
            <span style={{ fontFamily:"DM Mono", fontSize:12, color:c.color }}>{fmt(c.a)} / {fmt(c.b)}</span>
          </div>
          <ProgressBar pct={(c.a/c.b)*100} color={c.color} height={6} />
        </div>
      ))}
    </Card>
  </div>
);

// ── BILLS ──────────────────────────────────────────────────────────────────
const BILLS = [
  { n:"Co-op Maintenance", cat:"Housing", a:2840, f:"Monthly", due:"Jun 1", auto:true, used:"-" },
  { n:"BoA Mortgage", cat:"Loan", a:3520, f:"Monthly", due:"Jun 1", auto:true, used:"-" },
  { n:"HELOC Payment", cat:"Loan", a:285, f:"Monthly", due:"Jun 5", auto:true, used:"-" },
  { n:"Con Edison", cat:"Utility", a:185, f:"Monthly", due:"May 8", auto:true, used:"-" },
  { n:"Verizon Fios", cat:"Utility", a:120, f:"Monthly", due:"May 12", auto:true, used:"daily" },
  { n:"NYSC Gym", cat:"Membership", a:89, f:"Monthly", due:"May 20", auto:true, used:"3×/wk" },
  { n:"Netflix", cat:"Subscription", a:23, f:"Monthly", due:"May 15", auto:true, used:"4×/mo" },
  { n:"Disney+", cat:"Subscription", a:14, f:"Monthly", due:"May 22", auto:true, used:"never", flag:"Cancel?" },
  { n:"NYT Digital", cat:"Subscription", a:25, f:"Monthly", due:"May 28", auto:true, used:"daily" },
  { n:"iCloud+ 2TB", cat:"Subscription", a:10, f:"Monthly", due:"May 18", auto:true, used:"daily" },
  { n:"Auto Insurance", cat:"Insurance", a:1840, f:"Annual", due:"Jun 4", auto:false, used:"-" },
  { n:"NYC Property Tax", cat:"Tax", a:4200, f:"Quarterly", due:"Jul 1", auto:false, used:"-" },
];
const Bills = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Bills & Subscriptions" sub={`${BILLS.length} active · 1 flagged for review`} action={<button style={btn}>+ Add Bill</button>} />
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="Monthly" value={fmt(7411)} sub="Recurring outflow"/>
      <StatCard label="Annual Total" value={fmt(94920)} color={C.gold}/>
      <StatCard label="Auto-Pay" value="10 of 12" color={C.teal}/>
      <StatCard label="Flagged" value="1" sub="Disney+ unused" color={C.amber}/>
    </div>
    <Card style={{ padding:0 }}>
      <div style={{ display:"grid", gridTemplateColumns:"1.6fr 1fr 100px 100px 80px 100px 100px",
        padding:"14px 24px", borderBottom:`1px solid ${C.border}`,
        color:C.sub, fontSize:10, letterSpacing:1.5, textTransform:"uppercase", fontFamily:"Outfit" }}>
        <span>Bill</span><span>Category</span><span style={{textAlign:"right"}}>Amount</span><span style={{textAlign:"right"}}>Frequency</span><span style={{textAlign:"right"}}>Auto</span><span style={{textAlign:"right"}}>Usage</span><span style={{textAlign:"right"}}>Next Due</span>
      </div>
      {BILLS.map((b,i) => (
        <div key={i} style={{ display:"grid", gridTemplateColumns:"1.6fr 1fr 100px 100px 80px 100px 100px",
          padding:"14px 24px", borderBottom: i<BILLS.length-1?`1px solid ${C.border}`:"none", alignItems:"center" }}>
          <div>
            <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text }}>{b.n}</span>
            {b.flag && <span style={{ marginLeft:8, fontFamily:"Outfit", fontSize:10, color:C.amber }}>⚠ {b.flag}</span>}
          </div>
          <Pill color={C.sub} bg={C.surface}>{b.cat}</Pill>
          <span style={{ fontFamily:"DM Mono", fontSize:13, color:C.text, textAlign:"right" }}>{fmt(b.a)}</span>
          <span style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, textAlign:"right" }}>{b.f}</span>
          <span style={{ fontSize:14, textAlign:"right", color:b.auto?C.green:C.muted }}>{b.auto?"●":"○"}</span>
          <span style={{ fontFamily:"Outfit", fontSize:11, color:b.used==="never"?C.amber:C.sub, textAlign:"right" }}>{b.used}</span>
          <span style={{ fontFamily:"DM Mono", fontSize:11, color:C.sub, textAlign:"right" }}>{b.due}</span>
        </div>
      ))}
    </Card>
  </div>
);

// ── INCOME ─────────────────────────────────────────────────────────────────
const Income = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Income & Equity" sub="Paychecks · Bonuses · RSU vests" />
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="YTD Income" value={fmt(178000)} sub="Through Apr" color={C.gold}/>
      <StatCard label="Projected Annual" value={fmt(485000)} sub="Including bonus"/>
      <StatCard label="Vesting This Year" value={fmt(82000)} sub="RSUs · Andre" color={C.teal}/>
      <StatCard label="Side Income YTD" value={fmt(8400)} sub="Consulting" color={C.lavender}/>
    </div>
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <Card style={{ flex:1, minWidth:340 }}>
        <SectionHeader title="Vesting Schedule (Andre)" sub="Acme Smoked Fish · RSUs" />
        {[
          {d:"Jun 15, 2026", s:120, p:680, v:81600, vested:false, blackout:true},
          {d:"Sep 15, 2026", s:120, p:680, v:81600, vested:false},
          {d:"Dec 15, 2026", s:120, p:680, v:81600, vested:false, blackout:true},
          {d:"Mar 15, 2027", s:120, p:680, v:81600, vested:false},
          {d:"Jun 15, 2027", s:120, p:680, v:81600, vested:false},
        ].map((v,i) => (
          <div key={i} style={{ display:"grid", gridTemplateColumns:"100px 60px 100px 1fr 100px",
            padding:"10px 0", borderBottom: i<4?`1px solid ${C.border}`:"none", alignItems:"center", gap:10 }}>
            <span style={{ fontFamily:"DM Mono", fontSize:11, color:C.sub }}>{v.d}</span>
            <span style={{ fontFamily:"DM Mono", fontSize:12, color:C.text }}>{v.s} sh</span>
            <span style={{ fontFamily:"DM Mono", fontSize:12, color:C.text }}>@{fmt(v.p)}</span>
            <span style={{ fontFamily:"Outfit", fontSize:10, color:v.blackout?C.amber:C.sub }}>{v.blackout?"Blackout active":"Open window"}</span>
            <span style={{ fontFamily:"DM Mono", fontSize:13, color:C.gold, textAlign:"right" }}>{fmt(v.v)}</span>
          </div>
        ))}
      </Card>
      <Card style={{ flex:1, minWidth:280 }}>
        <SectionHeader title="Income Breakdown" sub="Last 12 months" />
        {[
          {l:"Salary (Andre)", v:285000, c:C.gold},
          {l:"Salary (Spouse)", v:142000, c:C.teal},
          {l:"Bonus", v:48000, c:C.amber},
          {l:"RSU Vests", v:82000, c:C.lavender},
          {l:"Dividends/Interest", v:14000, c:C.mint},
          {l:"Side Income", v:8400, c:C.rose},
        ].map((x,i) => (
          <div key={i} style={{ marginBottom:12 }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
              <span style={{ fontFamily:"Outfit", fontSize:12, color:C.text }}>{x.l}</span>
              <span style={{ fontFamily:"DM Mono", fontSize:12, color:x.c }}>{fmt(x.v)}</span>
            </div>
            <ProgressBar pct={(x.v/285000)*100} color={x.c} height={5} />
          </div>
        ))}
      </Card>
    </div>
  </div>
);

// ── INSURANCE ──────────────────────────────────────────────────────────────
const INSURANCE = [
  { type:"Term Life — Andre", carrier:"Northwestern Mutual", coverage:1500000, premium:1200, exp:"2042", insured:"Andre" },
  { type:"Term Life — Spouse", carrier:"Northwestern Mutual", coverage:1000000, premium:850, exp:"2042", insured:"Spouse" },
  { type:"Long-Term Disability", carrier:"Guardian", coverage:120000, premium:2400, exp:"-", insured:"Andre" },
  { type:"Umbrella Liability", carrier:"Chubb", coverage:3000000, premium:680, exp:"2026", insured:"Household" },
  { type:"HO-6 Co-op Insurance", carrier:"Liberty Mutual", coverage:450000, premium:1240, exp:"2026", insured:"Co-op" },
  { type:"Auto", carrier:"GEICO", coverage:300000, premium:1840, exp:"2026", insured:"Vehicles" },
  { type:"Health (Family)", carrier:"Aetna PPO", coverage:0, premium:14400, exp:"-", insured:"Family" },
];
const Insurance = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Insurance & Risk" sub="7 active policies · No coverage gaps detected" action={<button style={btn}>+ Add Policy</button>} />
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="Life Coverage" value={fmtK(2500000)} sub="Combined · 5.2× income" color={C.gold}/>
      <StatCard label="Coverage Gap" value="$0" sub="✓ Above 5× income" color={C.green}/>
      <StatCard label="Annual Premium" value={fmt(22610)} />
      <StatCard label="Umbrella" value={fmtK(3000000)} sub="Liability protection" color={C.teal}/>
    </div>
    <Card style={{ padding:0 }}>
      <div style={{ display:"grid", gridTemplateColumns:"1.6fr 1.2fr 100px 1fr 100px 80px",
        padding:"14px 24px", borderBottom:`1px solid ${C.border}`,
        color:C.sub, fontSize:10, letterSpacing:1.5, textTransform:"uppercase", fontFamily:"Outfit" }}>
        <span>Policy</span><span>Carrier</span><span>Insured</span><span style={{textAlign:"right"}}>Coverage</span><span style={{textAlign:"right"}}>Premium</span><span style={{textAlign:"right"}}>Renews</span>
      </div>
      {INSURANCE.map((p,i) => (
        <div key={i} style={{ display:"grid", gridTemplateColumns:"1.6fr 1.2fr 100px 1fr 100px 80px",
          padding:"14px 24px", borderBottom: i<INSURANCE.length-1?`1px solid ${C.border}`:"none", alignItems:"center" }}>
          <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text }}>{p.type}</span>
          <span style={{ fontFamily:"Outfit", fontSize:12, color:C.sub }}>{p.carrier}</span>
          <Pill color={C.sub} bg={C.surface}>{p.insured}</Pill>
          <span style={{ fontFamily:"DM Mono", fontSize:13, color:C.text, textAlign:"right" }}>{p.coverage ? fmt(p.coverage) : "—"}</span>
          <span style={{ fontFamily:"DM Mono", fontSize:13, color:C.text, textAlign:"right" }}>{fmt(p.premium)}</span>
          <span style={{ fontFamily:"DM Mono", fontSize:11, color:C.sub, textAlign:"right" }}>{p.exp}</span>
        </div>
      ))}
    </Card>
    <Card>
      <SectionHeader title="Coverage Gap Analysis" />
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        {[
          { l:"Life (10× income)", c:2500000, n:4850000, g:-2350000 },
          { l:"Disability (60% income)", c:120000, n:171000, g:-51000 },
          { l:"Umbrella (= net worth)", c:3000000, n:1003020, g:1996980 },
          { l:"HO-6 (replacement cost)", c:450000, n:380000, g:70000 },
        ].map((x,i) => (
          <div key={i}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
              <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text }}>{x.l}</span>
              <span style={{ fontFamily:"DM Mono", fontSize:12, color:x.g>=0?C.green:C.amber }}>
                {fmt(x.c)} / needs {fmt(x.n)} · {x.g>=0?"✓ Covered":`Gap ${fmt(Math.abs(x.g))}`}
              </span>
            </div>
            <ProgressBar pct={(x.c/x.n)*100} color={x.g>=0?C.green:C.amber} height={5} />
          </div>
        ))}
      </div>
    </Card>
  </div>
);

// ── CREDIT ─────────────────────────────────────────────────────────────────
const Credit = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Credit Health" sub="Andre · Spouse · Combined household" />
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="Andre's Score" value="812" sub="Excellent · +4 vs. last month" color={C.green}/>
      <StatCard label="Spouse's Score" value="798" sub="Excellent · −2 vs. last month" color={C.green}/>
      <StatCard label="Utilization" value="3%" sub="of $84K limit · ✓ Low" color={C.teal}/>
      <StatCard label="Total Credit Available" value={fmtK(84000)} sub="Across 6 cards" />
    </div>
    <Card>
      <SectionHeader title="Credit Card Optimization" sub="Best card per category" />
      <div style={{ display:"grid", gridTemplateColumns:"1.4fr 1fr 1fr 1fr 100px", padding:"12px 0", borderBottom:`1px solid ${C.border}`, color:C.sub, fontSize:10, letterSpacing:1.5, textTransform:"uppercase", fontFamily:"Outfit" }}>
        <span>Card</span><span>Best For</span><span>Rewards Rate</span><span>Annual Fee</span><span style={{textAlign:"right"}}>Net Value</span>
      </div>
      {[
        {n:"Chase Sapphire Reserve", best:"Travel, Dining", rate:"3-10×", fee:550, net:1240},
        {n:"Amex Gold", best:"Groceries, Dining", rate:"4×", fee:325, net:880},
        {n:"Citi Double Cash", best:"Everything else", rate:"2%", fee:0, net:420},
        {n:"BoA Custom Cash", best:"Online Shopping", rate:"3%", fee:0, net:180},
        {n:"Amex Blue Cash", best:"Gas, Streaming", rate:"3-6%", fee:95, net:240},
        {n:"Chase Freedom Flex", best:"Rotating 5%", rate:"5%", fee:0, net:160},
      ].map((c,i) => (
        <div key={i} style={{ display:"grid", gridTemplateColumns:"1.4fr 1fr 1fr 1fr 100px", padding:"12px 0", borderBottom:i<5?`1px solid ${C.border}`:"none", alignItems:"center" }}>
          <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text }}>{c.n}</span>
          <span style={{ fontFamily:"Outfit", fontSize:11, color:C.sub }}>{c.best}</span>
          <span style={{ fontFamily:"DM Mono", fontSize:12, color:C.gold }}>{c.rate}</span>
          <span style={{ fontFamily:"DM Mono", fontSize:11, color:C.sub }}>{c.fee?fmt(c.fee):"None"}</span>
          <span style={{ fontFamily:"DM Mono", fontSize:13, color:C.green, textAlign:"right" }}>+{fmt(c.net)}</span>
        </div>
      ))}
    </Card>
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <Card style={{ flex:1, minWidth:280 }}>
        <SectionHeader title="Credit Freezes" />
        {["Equifax","Experian","TransUnion"].map((b,i) => (
          <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"12px 0", borderBottom:i<2?`1px solid ${C.border}`:"none" }}>
            <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text }}>{b}</span>
            <Pill color={C.green} bg={`${C.green}22`}>● Frozen</Pill>
          </div>
        ))}
      </Card>
      <Card style={{ flex:1, minWidth:280 }}>
        <SectionHeader title="Recent Activity" />
        {[
          {d:"Apr 28", e:"Score increase: 808 → 812", c:C.green},
          {d:"Apr 12", e:"Statement closed: $1,240 balance", c:C.sub},
          {d:"Mar 18", e:"Score increase: 805 → 808", c:C.green},
          {d:"Mar 02", e:"New inquiry: BoA refi", c:C.amber},
        ].map((a,i) => (
          <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:i<3?`1px solid ${C.border}`:"none" }}>
            <span style={{ fontFamily:"Outfit", fontSize:12, color:a.c }}>{a.e}</span>
            <span style={{ fontFamily:"DM Mono", fontSize:11, color:C.sub }}>{a.d}</span>
          </div>
        ))}
      </Card>
    </div>
  </div>
);

// ── ESTATE ─────────────────────────────────────────────────────────────────
const ESTATE = [
  { item:"Last Will & Testament", status:"complete", reviewed:"Jun 2024", due:"Jun 2027" },
  { item:"Revocable Living Trust", status:"complete", reviewed:"Jun 2024", due:"Jun 2027" },
  { item:"Healthcare Proxy", status:"complete", reviewed:"Jun 2024", due:"Jun 2027" },
  { item:"HIPAA Authorization", status:"complete", reviewed:"Jun 2024", due:"Jun 2027" },
  { item:"Durable Power of Attorney", status:"complete", reviewed:"Jun 2024", due:"Jun 2027" },
  { item:"Guardianship Designation", status:"complete", reviewed:"Jun 2024", due:"Jun 2027" },
  { item:"Beneficiary Audit (all accts)", status:"needs_review", reviewed:"Jan 2024", due:"Overdue" },
  { item:"Letter of Intent (Daughter)", status:"in_progress", reviewed:"-", due:"Aug 2026" },
  { item:"Digital Assets Inventory", status:"not_started", reviewed:"-", due:"Aug 2026" },
  { item:"In-Case-of-Emergency Document", status:"in_progress", reviewed:"-", due:"Aug 2026" },
];
const Estate = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Estate Planning" sub="Readiness checklist · Reviewed every 3 years" action={<button style={btn}>Generate ICE Document</button>} />
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="Items Complete" value="6 of 10" sub="60% ready" color={C.amber}/>
      <StatCard label="Needs Review" value="1" sub="Beneficiaries" color={C.red}/>
      <StatCard label="In Progress" value="2" />
      <StatCard label="Next Review" value="Jun 2027" sub="3-year cadence"/>
    </div>
    <Card style={{ padding:0 }}>
      {ESTATE.map((e,i) => {
        const colors = { complete:C.green, needs_review:C.red, in_progress:C.amber, not_started:C.muted };
        const labels = { complete:"COMPLETE", needs_review:"REVIEW NEEDED", in_progress:"IN PROGRESS", not_started:"NOT STARTED" };
        return (
          <div key={i} style={{ display:"grid", gridTemplateColumns:"1.6fr 130px 1fr 1fr",
            padding:"16px 24px", borderBottom: i<ESTATE.length-1?`1px solid ${C.border}`:"none", alignItems:"center", gap:12 }}>
            <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text }}>{e.item}</span>
            <Pill color={colors[e.status]} bg={`${colors[e.status]}22`}>{labels[e.status]}</Pill>
            <span style={{ fontFamily:"Outfit", fontSize:12, color:C.sub }}>Reviewed: {e.reviewed}</span>
            <span style={{ fontFamily:"Outfit", fontSize:12, color: e.due==="Overdue"?C.red:C.sub }}>{e.due}</span>
          </div>
        );
      })}
    </Card>
  </div>
);

// ── DOCUMENTS ──────────────────────────────────────────────────────────────
const Documents = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Document Vault" sub="Encrypted · 247 documents · 1.4 GB used" action={<button style={btn}>+ Upload</button>} />
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="Documents" value="247" />
      <StatCard label="Storage" value="1.4 GB" sub="of 10 GB"/>
      <StatCard label="Expiring Soon" value="2" sub="Passport, Auto Ins" color={C.amber}/>
      <StatCard label="HSA Reimbursable" value={fmtK(8400)} sub="Lifetime tax-free" color={C.green}/>
    </div>
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:14 }}>
      {[
        {n:"Statements", c:124, ic:"📄"},
        {n:"Tax Documents", c:48, ic:"🗂"},
        {n:"Estate", c:8, ic:"⚖"},
        {n:"Insurance Policies", c:12, ic:"🛡"},
        {n:"Medical Receipts (HSA)", c:34, ic:"⚕"},
        {n:"Property", c:9, ic:"🏠"},
        {n:"Identity (passports, etc.)", c:8, ic:"🪪"},
        {n:"Daughter", c:4, ic:"🎓"},
      ].map((d,i) => (
        <Card key={i} style={{ cursor:"pointer", textAlign:"center", padding:20 }}>
          <div style={{ fontSize:28, marginBottom:8 }}>{d.ic}</div>
          <div style={{ fontFamily:"Fraunces", fontSize:14, color:C.text, fontWeight:600 }}>{d.n}</div>
          <div style={{ fontFamily:"DM Mono", fontSize:11, color:C.sub, marginTop:4 }}>{d.c} files</div>
        </Card>
      ))}
    </div>
    <Card>
      <SectionHeader title="Expiring Documents" />
      {[
        {n:"Andre's Passport", t:"Identity", exp:"Aug 2026", days:120, sev:"warning"},
        {n:"Auto Insurance Policy", t:"Insurance", exp:"Jun 4, 2026", days:33, sev:"warning"},
      ].map((d,i) => (
        <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"12px 0", borderBottom:i<1?`1px solid ${C.border}`:"none", alignItems:"center" }}>
          <div>
            <div style={{ fontFamily:"Outfit", fontSize:13, color:C.text }}>{d.n}</div>
            <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginTop:2 }}>{d.t}</div>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontFamily:"DM Mono", fontSize:13, color:C.amber }}>{d.exp}</div>
            <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginTop:2 }}>{d.days} days</div>
          </div>
        </div>
      ))}
    </Card>
  </div>
);

// ── CHARITY ────────────────────────────────────────────────────────────────
const Charity = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Charitable Giving" sub="2026 · 7 organizations" action={<button style={btn}>+ Record Donation</button>} />
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="YTD Donated" value={fmtK(12400)} color={C.gold}/>
      <StatCard label="Annual Target" value={fmtK(20000)} sub="62% complete"/>
      <StatCard label="Tax Deductible" value={fmtK(12400)} sub="Itemized"/>
      <StatCard label="DAF Balance" value={fmtK(8200)} sub="Available" color={C.teal}/>
    </div>
    <Card>
      <SectionHeader title="2026 Donations" />
      {[
        {n:"Doctors Without Borders", a:2500, t:"Cash", d:"Apr 2026"},
        {n:"NYC Food Bank", a:1500, t:"Cash", d:"Mar 2026"},
        {n:"DAF Contribution", a:5000, t:"Stock (VOO)", d:"Feb 2026"},
        {n:"Local Public School Foundation", a:1500, t:"Cash", d:"Feb 2026"},
        {n:"American Red Cross", a:1000, t:"Cash", d:"Jan 2026"},
        {n:"Wikipedia Foundation", a:500, t:"Cash", d:"Jan 2026"},
        {n:"Religious Community", a:400, t:"Cash", d:"Jan 2026"},
      ].map((d,i) => (
        <div key={i} style={{ display:"grid", gridTemplateColumns:"2fr 100px 1fr 100px",
          padding:"14px 0", borderBottom:i<6?`1px solid ${C.border}`:"none", alignItems:"center" }}>
          <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text }}>{d.n}</span>
          <span style={{ fontFamily:"DM Mono", fontSize:13, color:C.gold }}>{fmt(d.a)}</span>
          <Pill color={C.sub} bg={C.surface}>{d.t}</Pill>
          <span style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, textAlign:"right" }}>{d.d}</span>
        </div>
      ))}
    </Card>
  </div>
);

// ── AI ─────────────────────────────────────────────────────────────────────
const AI = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Ask Claude" sub="Cross-data Q&A · Your API key · Read-only" action={<button style={btnGhost}>Settings</button>} />
    <Card style={{ display:"flex", flexDirection:"column", gap:14, minHeight:500 }}>
      <div style={{ flex:1, display:"flex", flexDirection:"column", gap:14 }}>
        <div style={{ display:"flex", alignItems:"start", gap:12 }}>
          <div style={{ width:32, height:32, borderRadius:"50%", background:C.surface, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, color:C.sub, flexShrink:0 }}>A</div>
          <div style={{ background:C.surface, padding:14, borderRadius:10, maxWidth:"80%" }}>
            <div style={{ fontFamily:"Outfit", fontSize:13, color:C.text, lineHeight:1.5 }}>Should we be doing a Roth conversion this year?</div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"start", gap:12 }}>
          <div style={{ width:32, height:32, borderRadius:"50%", background:`${C.gold}33`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, color:C.gold, flexShrink:0, fontFamily:"Fraunces", fontWeight:700 }}>✦</div>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:"Outfit", fontSize:13, color:C.text, lineHeight:1.6 }}>
              <p style={{ marginBottom:10 }}>Looking at your situation:</p>
              <p style={{ marginBottom:10 }}>You're in the 32% federal bracket this year with combined income of ~$485K. Your traditional 401(k) balance is $612,800. Roth conversion now would be expensive — you'd pay top-bracket tax on whatever you convert.</p>
              <p style={{ marginBottom:10 }}><strong style={{ color:C.gold }}>Better timing:</strong> wait for your sabbatical year (modeled in scenarios as 2030) when income drops to near-zero. Converting $50K/year during a sabbatical would cost you maybe 12-22% federal vs. 32% today.</p>
              <p style={{ marginBottom:10 }}><strong style={{ color:C.gold }}>Alternative now:</strong> the mega backdoor Roth in your 401(k) — your plan supports it, and you haven't used the $46,500 capacity this year. That's after-tax money you can convert immediately, no tax impact.</p>
              <p style={{ color:C.sub, fontSize:11, fontStyle:"italic", marginTop:14 }}>Tools used: get_tax_projection(2026), get_retirement_plans, get_scenarios. Not financial advice — verify with your CPA.</p>
            </div>
            <div style={{ display:"flex", gap:8, marginTop:14 }}>
              <button style={{ ...btnGhost, fontSize:11 }}>👍 Helpful</button>
              <button style={{ ...btnGhost, fontSize:11 }}>Save to Notes</button>
              <button style={{ ...btnGhost, fontSize:11 }}>Add to Decisions</button>
            </div>
          </div>
        </div>
      </div>
      <div style={{ display:"flex", gap:8, marginTop:8, flexWrap:"wrap" }}>
        {[
          "Summarize this month's changes",
          "How am I doing on FIRE goals?",
          "Should I rebalance now?",
          "Tax-loss harvest opportunities?",
          "Generate this week's money date agenda",
        ].map((s,i) => (
          <div key={i} style={{ background:C.surface, padding:"6px 12px", borderRadius:20, fontFamily:"Outfit", fontSize:11, color:C.sub, cursor:"pointer", border:`1px solid ${C.border}` }}>{s}</div>
        ))}
      </div>
      <div style={{ background:C.surface, padding:"14px 16px", borderRadius:10, border:`1px solid ${C.border}`, display:"flex", gap:10, alignItems:"center" }}>
        <input placeholder="Ask anything about your finances..." style={{ flex:1, background:"transparent", border:"none", color:C.text, fontFamily:"Outfit", fontSize:13, outline:"none" }} />
        <button style={btn}>Send</button>
      </div>
      <div style={{ fontFamily:"Outfit", fontSize:10, color:C.muted, lineHeight:1.5 }}>
        Claude is not a financial advisor. Verify before acting. Uses your API key (encrypted). Tokens used today: 4,820 of 33,000 cap. Cost today: $0.18.
      </div>
    </Card>
  </div>
);

// ── Skip the rest in interest of space — keep the existing pages ────────────
const Accounts = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Accounts" sub="12 accounts · 7 institutions · 4 sync sources" action={<button style={btn}>+ Add Account</button>} />
    <Card style={{ padding:0 }}>
      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 80px", padding:"14px 24px", borderBottom:`1px solid ${C.border}`,
        color:C.sub, fontSize:10, letterSpacing:1.5, textTransform:"uppercase", fontFamily:"Outfit" }}>
        <span>Account</span><span>Institution</span><span>Type</span><span style={{textAlign:"right"}}>Balance</span><span style={{textAlign:"right"}}>Sync</span>
      </div>
      {ACCOUNTS.map((a,i) => (
        <div key={a.id} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 80px",
          padding:"14px 24px", borderBottom: i<ACCOUNTS.length-1?`1px solid ${C.border}`:"none", alignItems:"center" }}>
          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
            <div style={{ width:3, height:32, background:a.color, borderRadius:2 }} />
            <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text }}>{a.name}</span>
          </div>
          <span style={{ fontFamily:"Outfit", fontSize:12, color:C.sub }}>{a.inst}</span>
          <Pill color={a.color} bg={`${a.color}22`}>{a.type}</Pill>
          <span style={{ fontFamily:"DM Mono", fontSize:13, color:a.value<0?C.red:C.text, textAlign:"right" }}>{fmt(a.value)}</span>
          <span style={{ fontFamily:"Outfit", fontSize:10, textAlign:"right", color:a.sync==="manual"?C.muted:a.sync==="plaid"?C.teal:C.lavender }}>
            {a.sync==="manual"?"Manual":a.sync==="plaid"?"Plaid":"SnapTrade"}
          </span>
        </div>
      ))}
    </Card>
  </div>
);
const Holdings = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Holdings" sub="103 positions · concentration & overlap analysis" />
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="Total Value" value={fmtK(1312020)} color={C.gold} />
      <StatCard label="Cost Basis" value={fmtK(984500)} />
      <StatCard label="Unrealized" value={fmtK(327520)} color={C.green} sub="+33.3%" />
      <StatCard label="Tax Cost Ratio" value="0.42%" sub="Annual drag" />
    </div>
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <Card style={{ flex:1, minWidth:280 }}>
        <SectionHeader title="Concentration" sub="Top 5 positions = 36%" />
        {[{t:"VOO",v:14.4,c:C.gold},{t:"VTI",v:7.5,c:C.gold},{t:"VXUS",v:6.7,c:C.teal},{t:"NVDA",v:4.2,c:C.amber},{t:"BND",v:5.5,c:C.lavender}].map((h,i) => (
          <div key={i} style={{ marginBottom:10 }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
              <span style={{ fontFamily:"DM Mono", fontSize:12, color:C.text, fontWeight:500 }}>{h.t}</span>
              <span style={{ fontFamily:"DM Mono", fontSize:12, color:C.text }}>{h.v}%</span>
            </div>
            <ProgressBar pct={h.v*4} color={h.c} height={5} />
          </div>
        ))}
      </Card>
      <Card style={{ flex:1, minWidth:280 }}>
        <SectionHeader title="Holdings Overlap" sub="Look-through to underlying" />
        <div style={{ background:`${C.amber}11`, padding:12, borderRadius:8, marginBottom:14 }}>
          <div style={{ fontFamily:"Outfit", fontSize:12, color:C.amber, fontWeight:500 }}>◆ Overlap detected</div>
          <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginTop:4, lineHeight:1.5 }}>
            Apple appears in VOO + VTI + AAPL direct = <strong>12.4% effective exposure</strong>
          </div>
        </div>
        <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, lineHeight:1.5 }}>
          VOO + VTI + mega-caps = 31% top-10 exposure
        </div>
      </Card>
      <Card style={{ flex:1, minWidth:280 }}>
        <SectionHeader title="Annual Fee Drag" />
        <div style={{ fontFamily:"DM Mono", fontSize:30, color:C.text, fontWeight:600 }}>{fmt(2840)}</div>
        <div style={{ fontFamily:"Outfit", fontSize:12, color:C.sub, marginTop:6 }}>Weighted ER: 0.22%</div>
        <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginTop:14, lineHeight:1.5 }}>
          Switching 1 active fund to its index alternative could save $1,200/yr.
        </div>
      </Card>
    </div>
  </div>
);
const Investments = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Investment Analysis" sub="Performance · risk · opportunities" />
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="YTD Return" value="+14.2%" color={C.green} sub="vs S&P +11.8%"/>
      <StatCard label="3-Yr Return" value="+11.8%" color={C.green} sub="Annualized"/>
      <StatCard label="Sharpe Ratio" value="1.42" sub="Risk-adjusted"/>
      <StatCard label="Max Drawdown" value="-18.4%" color={C.red} sub="Mar 2020"/>
    </div>
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <Card style={{ flex:1, minWidth:280 }}>
        <SectionHeader title="Rebalancing" />
        {[{a:"Sell",t:"NVDA",amt:18000,r:"Concentration"},{a:"Buy",t:"BND",amt:12000,r:"Bond target"},{a:"Buy",t:"VXUS",amt:6000,r:"Intl underweight"}].map((s,i) => (
          <div key={i} style={{ display:"flex", gap:12, padding:12, background:C.surface, borderRadius:8, marginBottom:8 }}>
            <Pill color={s.a==="Sell"?C.red:C.green} bg={`${s.a==="Sell"?C.red:C.green}22`}>{s.a}</Pill>
            <div style={{ flex:1 }}>
              <div style={{ fontFamily:"DM Mono", fontSize:13, color:C.text }}>{s.t} · {fmt(s.amt)}</div>
              <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginTop:2 }}>{s.r}</div>
            </div>
          </div>
        ))}
      </Card>
      <Card style={{ flex:1, minWidth:280 }}>
        <SectionHeader title="Dividend Calendar" sub="$1,840 next 30 days" />
        {[{d:"May 8",t:"AAPL",a:52},{d:"May 15",t:"MSFT",a:88},{d:"May 22",t:"VOO",a:680},{d:"May 28",t:"VTI",a:420},{d:"May 30",t:"BND",a:600}].map((d,i) => (
          <div key={i} style={{ display:"grid", gridTemplateColumns:"80px 80px 1fr", padding:"10px 0", borderBottom:i<4?`1px solid ${C.border}`:"none", alignItems:"center" }}>
            <span style={{ fontFamily:"Outfit", fontSize:12, color:C.sub }}>{d.d}</span>
            <span style={{ fontFamily:"DM Mono", fontSize:12, color:C.gold }}>{d.t}</span>
            <span style={{ fontFamily:"DM Mono", fontSize:13, color:C.text, textAlign:"right" }}>{fmt(d.a)}</span>
          </div>
        ))}
      </Card>
      <Card style={{ flex:1, minWidth:280 }}>
        <SectionHeader title="Stress Tests" />
        {[{s:"2008",d:-37,r:"22mo"},{s:"COVID 2020",d:-23,r:"5mo"},{s:"2022 Bear",d:-18,r:"14mo"},{s:"Job Loss 6mo",d:0,r:"14mo cash"}].map((t,i) => (
          <div key={i} style={{ display:"grid", gridTemplateColumns:"1.4fr 1fr 1fr", padding:"10px 0", borderBottom:i<3?`1px solid ${C.border}`:"none" }}>
            <span style={{ fontFamily:"Outfit", fontSize:12, color:C.text }}>{t.s}</span>
            <span style={{ fontFamily:"DM Mono", fontSize:12, color:t.d<0?C.red:C.sub, textAlign:"right" }}>{t.d===0?"—":`${t.d}%`}</span>
            <span style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, textAlign:"right" }}>{t.r}</span>
          </div>
        ))}
      </Card>
    </div>
  </div>
);
const RealEstate = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Housing" sub="215 W 84th St, Apt 7C · Co-op" />
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="Current Value" value={fmtK(1450000)} sub="Zillow" color={C.gold}/>
      <StatCard label="Equity" value={fmtK(830000)} color={C.green} sub="57% LTV"/>
      <StatCard label="Mortgage" value={fmtK(620000)} sub="3.25% · 22 yrs left" color={C.red}/>
      <StatCard label="HELOC" value={fmtK(45000)} sub="6.5% variable" color={C.red}/>
    </div>
    <Card>
      <SectionHeader title="Co-op Details" />
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:20 }}>
        {[
          { l:"Type", v:"Co-op (Pre-war)" },
          { l:"Shares", v:"412 of 16,800" },
          { l:"Ownership", v:"2.45%" },
          { l:"Purchased", v:"Jun 2018 · $985K" },
          { l:"Adjusted Basis", v:"$1.067M", s:"+$82K improvements" },
          { l:"Flip Tax", v:"2% of profit" },
          { l:"Underlying Mortgage", v:"$38.4K share" },
          { l:"Maintenance", v:"$2,840/mo" },
        ].map((x,i) => (
          <div key={i}>
            <div style={{ fontFamily:"Outfit", fontSize:9, color:C.sub, letterSpacing:1.5, textTransform:"uppercase" }}>{x.l}</div>
            <div style={{ fontFamily:"DM Mono", fontSize:14, color:C.text, marginTop:4 }}>{x.v}</div>
            {x.s && <div style={{ fontFamily:"Outfit", fontSize:10, color:C.muted, marginTop:2 }}>{x.s}</div>}
          </div>
        ))}
      </div>
    </Card>
  </div>
);
const Family = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Family" sub="Daughter · Age 3" />
    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="529 Balance" value={fmtK(41000)} color={C.mint}/>
      <StatCard label="UTMA" value={fmtK(8400)} color={C.rose}/>
      <StatCard label="College Target" value={fmtK(300000)} sub="SUNY · 2041" color={C.gold}/>
      <StatCard label="Monthly Saving" value="$420"/>
    </div>
    <Card>
      <SectionHeader title="Future Milestones" />
      {[
        {y:"Age 5", m:"Activate allowance/chores ledger"},
        {y:"Age 8", m:"First savings account · banking education"},
        {y:"Age 14", m:"Custodial Roth IRA (when she earns income)"},
        {y:"Age 17", m:"FAFSA prep · 529 reallocation to bonds"},
        {y:"Age 18", m:"UTMA becomes hers · time for the talk"},
        {y:"Age 21", m:"Estate plan review · she's now financially adult"},
      ].map((m,i) => (
        <div key={i} style={{ display:"flex", gap:14, padding:"10px 0", borderBottom:i<5?`1px solid ${C.border}`:"none" }}>
          <span style={{ fontFamily:"DM Mono", fontSize:12, color:C.gold, width:60 }}>{m.y}</span>
          <span style={{ flex:1, fontFamily:"Outfit", fontSize:13, color:C.text }}>{m.m}</span>
        </div>
      ))}
    </Card>
  </div>
);
const Connections = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Integrations" sub="External account sync sources" action={<button style={btn}>+ Connect</button>} />
    <Card style={{ padding:0 }}>
      <div style={{ display:"grid", gridTemplateColumns:"100px 1.5fr 1fr 1fr 100px", padding:"14px 24px", borderBottom:`1px solid ${C.border}`,
        color:C.sub, fontSize:10, letterSpacing:1.5, textTransform:"uppercase", fontFamily:"Outfit" }}>
        <span>Provider</span><span>Institution</span><span>Accounts</span><span>Last Sync</span><span style={{textAlign:"right"}}>Status</span>
      </div>
      {[
        { p:"Plaid", i:"Bank of America", a:5, l:"2h", s:"active" },
        { p:"Plaid", i:"Marcus", a:1, l:"2h", s:"active" },
        { p:"Plaid", i:"Merrill Lynch", a:3, l:"6d", s:"needs_reauth" },
        { p:"SnapTrade", i:"Coinbase", a:1, l:"4h", s:"active" },
        { p:"Manual", i:"Fidelity HSA", a:1, l:"Apr 28", s:"manual" },
        { p:"Manual", i:"NY 529", a:1, l:"Apr 15", s:"manual" },
      ].map((c,i) => (
        <div key={i} style={{ display:"grid", gridTemplateColumns:"100px 1.5fr 1fr 1fr 100px", padding:"16px 24px", borderBottom:i<5?`1px solid ${C.border}`:"none", alignItems:"center" }}>
          <Pill color={c.p==="Plaid"?C.teal:c.p==="SnapTrade"?C.lavender:C.muted} bg={C.surface}>{c.p}</Pill>
          <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text }}>{c.i}</span>
          <span style={{ fontFamily:"DM Mono", fontSize:12, color:C.sub }}>{c.a} accts</span>
          <span style={{ fontFamily:"Outfit", fontSize:12, color:C.sub }}>{c.l}</span>
          <span style={{ fontFamily:"Outfit", fontSize:11, fontWeight:500, textAlign:"right",
            color: c.s==="active"?C.green:c.s==="needs_reauth"?C.amber:C.muted }}>
            {c.s==="active"?"● Active":c.s==="needs_reauth"?"⚠ Reauth":"Manual"}
          </span>
        </div>
      ))}
    </Card>
  </div>
);

// ── MONEY DATE (NEW) ────────────────────────────────────────────────────────
// FIRE community ritual: couples sit down monthly to review finances together
const MoneyDate = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Money Date" sub="Your monthly financial review · Last: Apr 4, 2026"
      action={<button style={btn}>Start This Month's Review</button>} />

    <Card>
      <SectionHeader title="May 2026 Agenda" sub="Auto-generated · Customize before starting" />
      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        {[
          { t:"Net worth update", body:"Crossed $1M milestone! Review trajectory and celebrate.", time:"5 min", done:false },
          { t:"Savings rate check", body:"Currently 42% — on track. Discuss any planned changes.", time:"5 min", done:false },
          { t:"Q2 estimated tax decision", body:"$8,400 due Jun 15. Roth conversion this year or wait for sabbatical?", time:"15 min", done:false, priority:true },
          { t:"NVDA concentration", body:"Now 11.4% of taxable. Trim or hold?", time:"10 min", done:false },
          { t:"Beneficiary audit (overdue 16 mo)", body:"Walk through all account beneficiaries together.", time:"20 min", done:false, priority:true },
          { t:"Daughter's preschool deposit", body:"Spouse handled. Confirm and update budget category.", time:"3 min", done:true },
          { t:"Goals progress review", body:"Kitchen reno on track. Sabbatical fund needs +$500/mo to hit 2030.", time:"10 min", done:false },
          { t:"Wins, worries, surprises", body:"Open conversation. What did we learn this month?", time:"10 min", done:false },
        ].map((item,i) => (
          <div key={i} style={{ display:"flex", gap:14, padding:14, background:item.priority?`${C.amber}11`:C.surface, border:`1px solid ${item.priority?C.amber+"66":C.border}`, borderRadius:8 }}>
            <div style={{ width:18, height:18, borderRadius:4, border:`1.5px solid ${item.done?C.green:C.muted}`, background:item.done?C.green:"transparent", display:"flex", alignItems:"center", justifyContent:"center", color:C.bg, fontSize:11, marginTop:2, flexShrink:0 }}>{item.done?"✓":""}</div>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:4 }}>
                <span style={{ fontFamily:"Outfit", fontSize:13, color:item.done?C.muted:C.text, fontWeight:500, textDecoration:item.done?"line-through":"none" }}>{item.t}</span>
                {item.priority && <Pill color={C.amber} bg={`${C.amber}22`}>Priority</Pill>}
              </div>
              <div style={{ fontFamily:"Outfit", fontSize:12, color:C.sub, lineHeight:1.5 }}>{item.body}</div>
            </div>
            <span style={{ fontFamily:"DM Mono", fontSize:10, color:C.muted, whiteSpace:"nowrap", marginTop:2 }}>{item.time}</span>
          </div>
        ))}
      </div>
      <div style={{ marginTop:16, padding:14, background:C.bg, borderRadius:8, fontFamily:"Outfit", fontSize:11, color:C.sub, lineHeight:1.5 }}>
        Estimated total: <strong style={{color:C.gold}}>78 minutes</strong>. AI assembles this from your alerts, decisions due for revisit, and goals off-track. Edit before starting.
      </div>
    </Card>

    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <Card style={{ flex:1, minWidth:300 }}>
        <SectionHeader title="Past Sessions" />
        {[
          { d:"Apr 4, 2026", dur:"52 min", decisions:1, body:"Decided to delay car purchase. Reviewed Q1 tax payment. Daughter's preschool selection." },
          { d:"Mar 7, 2026", dur:"44 min", decisions:0, body:"Quarterly review. Net worth +$22K month over month. No major decisions." },
          { d:"Feb 2, 2026", dur:"68 min", decisions:2, body:"Decided to max mega backdoor Roth. Confirmed kitchen reno budget at $80K." },
          { d:"Jan 4, 2026", dur:"95 min", decisions:3, body:"Annual planning session. Set 2026 savings target, FIRE date, goals." },
        ].map((s,i) => (
          <div key={i} style={{ padding:"14px 0", borderBottom: i<3?`1px solid ${C.border}`:"none" }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
              <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text, fontWeight:500 }}>{s.d}</span>
              <div style={{ display:"flex", gap:8 }}>
                <Pill color={C.sub} bg={C.surface}>{s.dur}</Pill>
                {s.decisions > 0 && <Pill color={C.gold} bg={`${C.gold}22`}>{s.decisions} decisions</Pill>}
              </div>
            </div>
            <div style={{ fontFamily:"Outfit", fontSize:12, color:C.sub, lineHeight:1.4 }}>{s.body}</div>
          </div>
        ))}
      </Card>

      <Card style={{ flex:1, minWidth:300 }}>
        <SectionHeader title="Money Date Insights" sub="What we've learned" />
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div style={{ padding:14, background:C.surface, borderRadius:8 }}>
            <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginBottom:6 }}>Average duration</div>
            <div style={{ fontFamily:"DM Mono", fontSize:24, color:C.gold }}>62 min</div>
          </div>
          <div style={{ padding:14, background:C.surface, borderRadius:8 }}>
            <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginBottom:6 }}>Cadence</div>
            <div style={{ fontFamily:"DM Mono", fontSize:24, color:C.text }}>Monthly · 1st Sat</div>
            <div style={{ fontFamily:"Outfit", fontSize:10, color:C.muted, marginTop:4 }}>15 sessions held in 16 months · 94%</div>
          </div>
          <div style={{ padding:14, background:C.surface, borderRadius:8 }}>
            <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginBottom:6 }}>Most discussed topic</div>
            <div style={{ fontFamily:"Outfit", fontSize:14, color:C.text, fontWeight:500 }}>Daughter's college savings</div>
            <div style={{ fontFamily:"Outfit", fontSize:10, color:C.muted, marginTop:4 }}>11 of 15 sessions</div>
          </div>
        </div>
      </Card>
    </div>
  </div>
);

// ── ASSET LOCATION OPTIMIZER (NEW) ──────────────────────────────────────────
// Where to hold what for tax efficiency. Almost no app does this.
const AssetLocation = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Asset Location" sub="Tax-efficient placement of holdings across account types" />

    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="Tax Drag (Current)" value="0.42%" sub="Annual" color={C.amber}/>
      <StatCard label="Tax Drag (Optimized)" value="0.18%" sub="If relocated" color={C.green}/>
      <StatCard label="Annual Savings" value={fmt(3150)} sub="From relocation" color={C.gold}/>
      <StatCard label="Lifetime Savings" value={fmtK(264000)} sub="Compounded · 35 yrs" color={C.gold}/>
    </div>

    <Card>
      <SectionHeader title="Optimization Recommendations" sub="Move to most tax-advantaged location" />
      <div style={{ background:`${C.gold}11`, padding:14, borderRadius:8, marginBottom:18, fontSize:12, fontFamily:"Outfit", color:C.sub, lineHeight:1.6 }}>
        Tax-inefficient assets (bonds, REITs, actively managed funds) belong in <strong style={{color:C.text}}>tax-advantaged</strong> accounts (401k, IRA, HSA). Tax-efficient assets (broad index ETFs, individual stocks held long-term) belong in <strong style={{color:C.text}}>taxable</strong> accounts. The savings compound over decades.
      </div>
      {[
        { what:"BND (bonds)", from:"Taxable", fromV:46000, to:"401(k)", action:"Move", impact:1240, urgency:"high" },
        { what:"VNQ (REITs)", from:"Taxable", fromV:18500, to:"Roth IRA", action:"Move", impact:780, urgency:"high" },
        { what:"VTSAX → VTI", from:"Taxable", fromV:124000, to:"Same account", action:"Convert", impact:340, urgency:"medium" },
        { what:"VOO (S&P 500)", from:"401(k)", fromV:182000, to:"Taxable", action:"Swap", impact:540, urgency:"medium" },
        { what:"Active mutual funds", from:"Taxable", fromV:32000, to:"401(k)", action:"Move", impact:250, urgency:"low" },
      ].map((r,i) => (
        <div key={i} style={{ display:"grid", gridTemplateColumns:"1.4fr 1fr 1fr 100px 100px 80px", padding:"14px 0", borderBottom:i<4?`1px solid ${C.border}`:"none", alignItems:"center", gap:12 }}>
          <div>
            <div style={{ fontFamily:"Outfit", fontSize:13, color:C.text, fontWeight:500 }}>{r.what}</div>
            <div style={{ fontFamily:"DM Mono", fontSize:11, color:C.sub, marginTop:2 }}>{fmt(r.fromV)}</div>
          </div>
          <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub }}>From: {r.from}</div>
          <div style={{ fontFamily:"Outfit", fontSize:11, color:C.green }}>→ {r.to}</div>
          <Pill color={C.gold} bg={`${C.gold}22`}>{r.action}</Pill>
          <span style={{ fontFamily:"DM Mono", fontSize:13, color:C.green, textAlign:"right" }}>+{fmt(r.impact)}/yr</span>
          <Pill color={r.urgency==="high"?C.red:r.urgency==="medium"?C.amber:C.muted} bg={C.surface}>{r.urgency}</Pill>
        </div>
      ))}
    </Card>

    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <Card style={{ flex:1, minWidth:280 }}>
        <SectionHeader title="Current Allocation by Tax Type" />
        <PieChart width={250} height={200}>
          <Pie data={[
            { name:"Taxable", v:487, color:C.gold },
            { name:"Tax-deferred (401k)", v:613, color:C.teal },
            { name:"Tax-free (Roth/HSA)", v:171, color:C.green },
            { name:"Education (529)", v:41, color:C.mint },
          ]} cx={125} cy={100} innerRadius={50} outerRadius={90} dataKey="v" strokeWidth={0}>
            {[C.gold, C.teal, C.green, C.mint].map((c,i) => <Cell key={i} fill={c} />)}
          </Pie>
        </PieChart>
        <div style={{ display:"flex", flexDirection:"column", gap:8, marginTop:14 }}>
          {[
            { n:"Taxable", v:487, p:36, c:C.gold },
            { n:"Tax-deferred (401k)", v:613, p:46, c:C.teal },
            { n:"Tax-free (Roth/HSA)", v:171, p:13, c:C.green },
            { n:"Education (529)", v:41, p:3, c:C.mint },
          ].map((r,i) => (
            <div key={i} style={{ display:"flex", justifyContent:"space-between", fontFamily:"Outfit", fontSize:12 }}>
              <span style={{ display:"flex", gap:8, alignItems:"center", color:C.text }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:r.c }} />
                {r.n}
              </span>
              <span style={{ fontFamily:"DM Mono", color:r.c }}>{r.p}%</span>
            </div>
          ))}
        </div>
      </Card>

      <Card style={{ flex:1, minWidth:280 }}>
        <SectionHeader title="Asset Location Heat Map" sub="What's in each account type" />
        <div style={{ overflow:"auto" }}>
          <table style={{ width:"100%", fontFamily:"Outfit", fontSize:11, borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ color:C.sub, fontSize:10, letterSpacing:1.5, textTransform:"uppercase" }}>
                <th style={{ textAlign:"left", padding:"8px 0" }}></th>
                <th style={{ padding:"8px 6px" }}>Bonds</th>
                <th style={{ padding:"8px 6px" }}>REITs</th>
                <th style={{ padding:"8px 6px" }}>Index</th>
                <th style={{ padding:"8px 6px" }}>Active</th>
                <th style={{ padding:"8px 6px" }}>Crypto</th>
              </tr>
            </thead>
            <tbody>
              {[
                { acc:"Taxable", c:[{v:"high",p:"Suboptimal"},{v:"high",p:"Suboptimal"},{v:"low",p:"Optimal"},{v:"med",p:"OK"},{v:"low",p:"Optimal"}] },
                { acc:"401(k)", c:[{v:"low",p:"Optimal"},{v:"low",p:"Optimal"},{v:"med",p:"OK"},{v:"low",p:"Optimal"},{v:"high",p:"N/A"}] },
                { acc:"Roth IRA", c:[{v:"med",p:"OK"},{v:"low",p:"Optimal"},{v:"low",p:"Optimal"},{v:"low",p:"Optimal"},{v:"med",p:"OK"}] },
                { acc:"HSA", c:[{v:"med",p:"OK"},{v:"low",p:"Optimal"},{v:"low",p:"Optimal"},{v:"low",p:"Optimal"},{v:"high",p:"N/A"}] },
              ].map((r,i) => (
                <tr key={i} style={{ borderTop:`1px solid ${C.border}` }}>
                  <td style={{ padding:"10px 0", color:C.text, fontWeight:500 }}>{r.acc}</td>
                  {r.c.map((cell,j) => (
                    <td key={j} style={{ padding:"6px", textAlign:"center" }}>
                      <div style={{
                        width:24, height:24, margin:"0 auto", borderRadius:4,
                        background: cell.v==="low"?C.green:cell.v==="med"?C.amber:C.red,
                        opacity:0.8
                      }} title={cell.p} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display:"flex", gap:14, marginTop:14, fontFamily:"Outfit", fontSize:10, color:C.sub }}>
          <span><span style={{ display:"inline-block", width:8, height:8, background:C.green, borderRadius:2, marginRight:4 }} />Optimal</span>
          <span><span style={{ display:"inline-block", width:8, height:8, background:C.amber, borderRadius:2, marginRight:4 }} />OK</span>
          <span><span style={{ display:"inline-block", width:8, height:8, background:C.red, borderRadius:2, marginRight:4 }} />Suboptimal</span>
        </div>
      </Card>
    </div>
  </div>
);

// ── SEQUENCE OF RETURNS RISK (NEW) ──────────────────────────────────────────
// The early-retirement killer. FIRE community knows about this; few apps visualize it.
const SequenceRisk = () => {
  const goodFirst = Array.from({length:30},(_,i) => ({ y:i, v: i<5 ? 1000000*Math.pow(1.10,i) : 1610510*Math.pow(1.04,i-5) - 50000*((Math.pow(1.04,i-5)-1)/0.04) }));
  const badFirst = Array.from({length:30},(_,i) => ({ y:i, v: i<5 ? 1000000*Math.pow(0.93,i) - 50000*i : 696000 + (i-5)*15000 - 12000*(i-5) }));
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
      <SectionHeader title="Sequence of Returns Risk" sub="Why timing of returns matters as much as average" />

      <Card>
        <div style={{ background:`${C.amber}11`, padding:14, borderRadius:8, marginBottom:18, fontSize:13, fontFamily:"Outfit", color:C.text, lineHeight:1.6 }}>
          Two retirees with identical 7% average returns can have wildly different outcomes if they retire at different times. Bad returns in the <strong style={{color:C.amber}}>first 5 years</strong> are devastating because withdrawals deplete a shrinking portfolio. This is why early retirees especially need a strategy.
        </div>
        <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
          <Card style={{ flex:1, minWidth:300, padding:18, background:C.surface, border:`1px solid ${C.green}66` }}>
            <div style={{ fontFamily:"Fraunces", fontSize:14, color:C.green, fontWeight:600, marginBottom:6 }}>Lucky: Good Returns First</div>
            <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginBottom:14 }}>10% returns Y1-5, then 4%</div>
            <ResponsiveContainer width="100%" height={140}>
              <AreaChart data={goodFirst}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.green} stopOpacity={0.4}/><stop offset="100%" stopColor={C.green} stopOpacity={0}/></linearGradient></defs>
                <XAxis dataKey="y" hide /><YAxis hide />
                <Area type="monotone" dataKey="v" stroke={C.green} strokeWidth={2} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
            <div style={{ fontFamily:"DM Mono", fontSize:18, color:C.green, marginTop:10 }}>Ends at $2.6M</div>
            <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginTop:4 }}>30 years of safe withdrawals</div>
          </Card>
          <Card style={{ flex:1, minWidth:300, padding:18, background:C.surface, border:`1px solid ${C.red}66` }}>
            <div style={{ fontFamily:"Fraunces", fontSize:14, color:C.red, fontWeight:600, marginBottom:6 }}>Unlucky: Bad Returns First</div>
            <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginBottom:14 }}>-7% Y1-5, then 4% (same 30-yr average)</div>
            <ResponsiveContainer width="100%" height={140}>
              <AreaChart data={badFirst}>
                <defs><linearGradient id="b" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.red} stopOpacity={0.4}/><stop offset="100%" stopColor={C.red} stopOpacity={0}/></linearGradient></defs>
                <XAxis dataKey="y" hide /><YAxis hide />
                <Area type="monotone" dataKey="v" stroke={C.red} strokeWidth={2} fill="url(#b)" />
              </AreaChart>
            </ResponsiveContainer>
            <div style={{ fontFamily:"DM Mono", fontSize:18, color:C.red, marginTop:10 }}>Depleted Year 22</div>
            <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, marginTop:4 }}>Same average, different result</div>
          </Card>
        </div>
      </Card>

      <Card>
        <SectionHeader title="Bond Tent Glidepath" sub="Pre-retirement strategy: temporarily increase bonds to soften the danger zone" />
        <div style={{ background:C.surface, padding:14, borderRadius:8, marginBottom:14, fontFamily:"Outfit", fontSize:11, color:C.sub, lineHeight:1.6 }}>
          The Kitces/Pfau "rising equity glidepath": shift toward bonds approaching retirement, then unwind back to equities as the danger zone passes. Limits damage in the early bad-luck years.
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={[
            { age:38, eq:90, bd:10 }, { age:42, eq:85, bd:15 }, { age:45, eq:80, bd:20 },
            { age:48, eq:70, bd:30 }, { age:50, eq:60, bd:40 }, { age:52, eq:55, bd:45, retire:true },
            { age:55, eq:60, bd:40 }, { age:58, eq:65, bd:35 }, { age:62, eq:70, bd:30 },
            { age:68, eq:75, bd:25 }, { age:75, eq:70, bd:30 },
          ]}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="age" tick={{fill:C.sub,fontSize:10}} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={(v) => `${v}%`} tick={{fill:C.sub,fontSize:10,fontFamily:"DM Mono"}} axisLine={false} tickLine={false} width={42} />
            <Tooltip content={<Tip />} />
            <Area type="monotone" dataKey="eq" stackId="a" name="Equities" stroke={C.gold} fill={C.gold} fillOpacity={0.6} strokeWidth={2} />
            <Area type="monotone" dataKey="bd" stackId="a" name="Bonds" stroke={C.lavender} fill={C.lavender} fillOpacity={0.6} strokeWidth={2} />
            <ReferenceLine x={52} stroke={C.amber} strokeDasharray="3 3" label={{ value:"FIRE", fill:C.amber, fontSize:10, position:"top" }} />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      <Card>
        <SectionHeader title="Defenses Against Sequence Risk" />
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:14 }}>
          {[
            { n:"Cash Bucket (2-3 years)", body:"Cover early withdrawals from cash so you don't sell equities low. Refill in good years.", status:"have", v:"$104K" },
            { n:"Flexible Spending", body:"Cut discretionary spending in down years (Guyton-Klinger guardrails).", status:"planned" },
            { n:"Bond Tent / Glidepath", body:"Temporarily heavier in bonds around retirement.", status:"planned" },
            { n:"Side Income / Barista", body:"Even small income reduces withdrawal need in early years.", status:"have", v:"$8.4K/yr" },
            { n:"Geographic Arbitrage", body:"Move to lower-COL area in down years.", status:"option" },
            { n:"Delayed Social Security", body:"Wait until 70 for max benefit; SS as longevity insurance.", status:"planned" },
          ].map((d,i) => {
            const statusColor = { have:C.green, planned:C.amber, option:C.lavender }[d.status];
            const statusLabel = { have:"In place", planned:"Planned", option:"Optional" }[d.status];
            return (
              <div key={i} style={{ padding:14, background:C.surface, borderRadius:8, border:`1px solid ${C.border}` }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                  <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text, fontWeight:500 }}>{d.n}</span>
                  <Pill color={statusColor} bg={`${statusColor}22`}>{statusLabel}</Pill>
                </div>
                <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, lineHeight:1.5 }}>{d.body}</div>
                {d.v && <div style={{ fontFamily:"DM Mono", fontSize:13, color:C.gold, marginTop:8 }}>{d.v}</div>}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

// ── SIDE INCOME / SELF-EMPLOYMENT (NEW) ──────────────────────────────────
const SideIncome = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
    <SectionHeader title="Side Income & Self-Employment" sub="Consulting · Freelance · Investments · Royalties"
      action={<button style={btn}>+ Log Income</button>} />

    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <StatCard label="YTD Side Income" value={fmt(8400)} color={C.lavender}/>
      <StatCard label="Quarterly Tax Reserve" value={fmt(2520)} sub="30% set aside" color={C.amber}/>
      <StatCard label="Solo 401(k) Capacity" value={fmt(7350)} sub="20% of net earnings" color={C.teal}/>
      <StatCard label="SEP-IRA Alternative" value={fmt(2100)} sub="Simpler · 25% cap"/>
    </div>

    <Card>
      <SectionHeader title="Income Sources" />
      {[
        { src:"Technical Consulting", ytd:6800, type:"1099-NEC", entity:"Sole prop", c:C.lavender },
        { src:"Speaking Engagements", ytd:1200, type:"1099-NEC", entity:"Sole prop", c:C.teal },
        { src:"Substack Subscriptions", ytd:400, type:"1099-K", entity:"Sole prop", c:C.gold },
      ].map((s,i) => (
        <div key={i} style={{ display:"grid", gridTemplateColumns:"2fr 100px 100px 1fr", padding:"14px 0", borderBottom:i<2?`1px solid ${C.border}`:"none", alignItems:"center", gap:12 }}>
          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
            <div style={{ width:3, height:24, background:s.c, borderRadius:2 }} />
            <span style={{ fontFamily:"Outfit", fontSize:13, color:C.text }}>{s.src}</span>
          </div>
          <span style={{ fontFamily:"DM Mono", fontSize:13, color:s.c }}>{fmt(s.ytd)}</span>
          <Pill color={C.sub} bg={C.surface}>{s.type}</Pill>
          <span style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, textAlign:"right" }}>{s.entity}</span>
        </div>
      ))}
    </Card>

    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
      <Card style={{ flex:1, minWidth:300 }}>
        <SectionHeader title="Solo 401(k) vs. SEP-IRA" sub="Self-employment retirement vehicles" />
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div style={{ padding:14, background:`${C.teal}11`, border:`1px solid ${C.teal}66`, borderRadius:8 }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
              <span style={{ fontFamily:"Fraunces", fontSize:14, color:C.teal, fontWeight:600 }}>Solo 401(k)</span>
              <Pill color={C.teal} bg={`${C.teal}22`}>Recommended</Pill>
            </div>
            <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, lineHeight:1.5, marginBottom:8 }}>
              Higher contribution capacity (employee + employer side). Allows Roth contributions. More setup.
            </div>
            <div style={{ fontFamily:"DM Mono", fontSize:13, color:C.text }}>2026 max: ~$7,350 from $8,400 net</div>
          </div>
          <div style={{ padding:14, background:C.surface, border:`1px solid ${C.border}`, borderRadius:8 }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
              <span style={{ fontFamily:"Fraunces", fontSize:14, color:C.text, fontWeight:600 }}>SEP-IRA</span>
              <Pill color={C.muted} bg={C.surface}>Alternative</Pill>
            </div>
            <div style={{ fontFamily:"Outfit", fontSize:11, color:C.sub, lineHeight:1.5, marginBottom:8 }}>
              Simpler setup. Employer-side only (25%). No Roth option. Best for very low side income.
            </div>
            <div style={{ fontFamily:"DM Mono", fontSize:13, color:C.text }}>2026 max: ~$2,100</div>
          </div>
        </div>
      </Card>

      <Card style={{ flex:1, minWidth:300 }}>
        <SectionHeader title="Quarterly Tax Strategy" sub="Avoid surprise April bill" />
        <div style={{ fontFamily:"Outfit", fontSize:12, color:C.sub, marginBottom:14, lineHeight:1.5 }}>
          Self-employment income has no withholding. We auto-reserve 30% of each payment into your tax-savings account.
        </div>
        {[
          { q:"Q1 2026", due:"Apr 15", amt:840, paid:true },
          { q:"Q2 2026", due:"Jun 15", amt:1010, paid:false, upcoming:true },
          { q:"Q3 2026", due:"Sep 15", amt:1010, paid:false },
          { q:"Q4 2026", due:"Jan 15 '27", amt:1010, paid:false },
        ].map((q,i) => (
          <div key={i} style={{ display:"grid", gridTemplateColumns:"1fr 100px 100px 80px", padding:"10px 0", borderBottom:i<3?`1px solid ${C.border}`:"none", alignItems:"center" }}>
            <span style={{ fontFamily:"Outfit", fontSize:13, color:q.upcoming?C.amber:C.text }}>{q.q}</span>
            <span style={{ fontFamily:"DM Mono", fontSize:11, color:C.sub }}>{q.due}</span>
            <span style={{ fontFamily:"DM Mono", fontSize:13, color:C.text, textAlign:"right" }}>{fmt(q.amt)}</span>
            <Pill color={q.paid?C.green:q.upcoming?C.amber:C.muted} bg={`${(q.paid?C.green:q.upcoming?C.amber:C.muted)}22`}>
              {q.paid?"Paid":q.upcoming?"Due":"Future"}
            </Pill>
          </div>
        ))}
      </Card>
    </div>
  </div>
);

// ── NAV ────────────────────────────────────────────────────────────────────
const NAV = [
  { id:"dashboard", label:"Dashboard", group:"Overview" },
  { id:"alerts", label:"Alerts", group:"Overview", badge:3 },
  { id:"messages", label:"Notes & Messages", group:"Overview", badge:1 },
  { id:"moneydate", label:"Money Date", group:"Overview" },
  { id:"fire", label:"FIRE", group:"Overview" },
  { id:"sequence", label:"Sequence Risk", group:"Overview" },
  { id:"accounts", label:"Accounts", group:"Wealth" },
  { id:"holdings", label:"Holdings", group:"Wealth" },
  { id:"investments", label:"Investments", group:"Wealth" },
  { id:"assetlocation", label:"Asset Location", group:"Wealth" },
  { id:"networth", label:"Net Worth", group:"Wealth" },
  { id:"goals", label:"Goals", group:"Planning" },
  { id:"retirement", label:"Retirement", group:"Planning" },
  { id:"tax", label:"Tax", group:"Planning" },
  { id:"scenarios", label:"Scenarios", group:"Planning" },
  { id:"cashflow", label:"Cash Flow", group:"Cash Flow" },
  { id:"bills", label:"Bills", group:"Cash Flow" },
  { id:"income", label:"Income", group:"Cash Flow" },
  { id:"sideincome", label:"Side Income", group:"Cash Flow" },
  { id:"realestate", label:"Housing", group:"Assets & Risk" },
  { id:"insurance", label:"Insurance", group:"Assets & Risk" },
  { id:"credit", label:"Credit", group:"Assets & Risk" },
  { id:"family", label:"Daughter", group:"Family" },
  { id:"estate", label:"Estate", group:"Family" },
  { id:"charity", label:"Charity", group:"Family" },
  { id:"documents", label:"Documents", group:"Records" },
  { id:"connections", label:"Integrations", group:"Records" },
  { id:"ai", label:"Ask Claude", group:"AI" },
];

export default function App() {
  const [page, setPage] = useState("dashboard");
  const pages = {
    dashboard: <Dashboard goTo={setPage} />, alerts: <Alerts />, messages: <Messages />,
    moneydate: <MoneyDate />, sequence: <SequenceRisk />,
    fire: <FIRE />, accounts: <Accounts />, holdings: <Holdings />, investments: <Investments />,
    assetlocation: <AssetLocation />,
    networth: <NetWorth />, goals: <Goals />, retirement: <Retirement />, tax: <Tax />,
    scenarios: <Scenarios />, cashflow: <CashFlow />, bills: <Bills />, income: <Income />,
    sideincome: <SideIncome />,
    realestate: <RealEstate />, insurance: <Insurance />, credit: <Credit />,
    family: <Family />, estate: <Estate />, charity: <Charity />,
    documents: <Documents />, connections: <Connections />, ai: <AI />,
  };
  const groups = [...new Set(NAV.map(n => n.group))];

  return (
    <>
      <style>{`@import url('${FONT}');* { box-sizing:border-box; margin:0; padding:0; }
        body { background:${C.bg}; }
        button:hover { opacity:0.85; }
        ::-webkit-scrollbar { width:6px; }
        ::-webkit-scrollbar-thumb { background:${C.border}; border-radius:3px; }
      `}</style>
      <div style={{ minHeight:"100vh", background:C.bg, color:C.text, fontFamily:"Outfit", display:"flex" }}>
        <aside style={{ width:240, background:C.surface, borderRight:`1px solid ${C.border}`, padding:"24px 0", flexShrink:0, position:"sticky", top:0, height:"100vh", overflowY:"auto" }}>
          <div style={{ padding:"0 24px", marginBottom:32 }}>
            <div style={{ fontFamily:"Fraunces", fontSize:22, color:C.gold, fontWeight:700, letterSpacing:-0.5 }}>Hearth</div>
            <div style={{ fontFamily:"Outfit", fontSize:10, color:C.sub, letterSpacing:2, textTransform:"uppercase", marginTop:2 }}>Family Finance OS</div>
          </div>
          {groups.map(g => (
            <div key={g} style={{ marginBottom:18 }}>
              <div style={{ padding:"0 24px", fontFamily:"Outfit", fontSize:9, color:C.muted, letterSpacing:2, textTransform:"uppercase", marginBottom:6 }}>{g}</div>
              {NAV.filter(n => n.group===g).map(n => (
                <button key={n.id} onClick={() => setPage(n.id)} style={{
                  display:"flex", justifyContent:"space-between", alignItems:"center",
                  width:"100%", textAlign:"left", background:"transparent", border:"none", cursor:"pointer",
                  padding:"7px 24px", fontFamily:"Outfit", fontSize:13,
                  color:page===n.id?C.gold:C.sub,
                  borderLeft: page===n.id?`2px solid ${C.gold}`:"2px solid transparent",
                  fontWeight:page===n.id?500:400, transition:"all 0.15s"
                }}>
                  <span>{n.label}</span>
                  {n.badge && <span style={{ background:C.red, color:"white", borderRadius:10, padding:"1px 7px", fontSize:9, fontFamily:"DM Mono" }}>{n.badge}</span>}
                </button>
              ))}
            </div>
          ))}
          <div style={{ padding:"12px 24px 0", borderTop:`1px solid ${C.border}`, marginTop:8 }}>
            <div style={{ fontFamily:"Outfit", fontSize:12, color:C.text, fontWeight:500 }}>Andre & Spouse</div>
            <div style={{ fontFamily:"Outfit", fontSize:10, color:C.sub, marginTop:2 }}>Manhattan · NY</div>
          </div>
        </aside>
        <main style={{ flex:1, padding:"32px 40px", maxWidth:1240, margin:"0 auto" }}>
          {pages[page]}
        </main>
      </div>
    </>
  );
}
